import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LogOut, Plus, Pencil, Trash2, Download, Users, Mail, Phone, MapPin, MessageCircle, Clock, LifeBuoy, FileText, Calendar, Star, DollarSign, Headphones, Settings, Menu, Key, Database, Lightbulb, Shield, Send, ChevronUp, ChevronDown, X, Eye, CreditCard, Bot, Vote, HardDrive, GitBranch, HandCoins, ClipboardList, ClipboardCheck, QrCode, Mic, Paperclip, Upload, Link2, UserMinus, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import ForceRefreshButton from "@/components/ForceRefreshButton";
import { DuesReceiptPopup, DuesReceiptHistory } from "@/components/DuesReceipts";
import { useOfficerTitles } from "@/hooks/useOfficerTitles";
import { useChapters } from "@/hooks/useChapters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHAPTER_ORDER = ["National", "AD", "HS", "HA"];
const TITLES = ["Prez", "VP", "S@A", "ENF", "SEC", "T", "CD", "CC", "CCLC", "MD", "PM", "(pm)", "COO", "CTO", "Brother", "Honorary"];

// Helper function to get the Nth occurrence of a weekday in a month
const getNthWeekdayOfMonth = (year, month, weekday, n) => {
  const date = new Date(year, month, 1);
  let count = 0;
  
  while (date.getMonth() === month) {
    if (date.getDay() === weekday) {
      count++;
      if (count === n) {
        return new Date(date);
      }
    }
    date.setDate(date.getDate() + 1);
  }
  return null;
};

// Helper function to get 1st and 3rd Wednesday of each month
const getMeetingDates = (year) => {
  const dates = [];
  for (let month = 0; month < 12; month++) {
    // Get 1st Wednesday (weekday 3 = Wednesday)
    const firstWed = getNthWeekdayOfMonth(year, month, 3, 1);
    // Get 3rd Wednesday
    const thirdWed = getNthWeekdayOfMonth(year, month, 3, 3);
    dates.push(firstWed, thirdWed);
  }
  return dates;
};

// Helper function to format date as MM/DD
const formatMeetingDate = (date) => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}`;
};

// Helper function to sort members by chapter and title
const sortMembers = (members) => {
  if (!Array.isArray(members)) return [];
  return [...members].sort((a, b) => {
    // First sort by chapter
    const chapterA = CHAPTER_ORDER.indexOf(a.chapter);
    const chapterB = CHAPTER_ORDER.indexOf(b.chapter);
    
    if (chapterA !== chapterB) {
      return chapterA - chapterB;
    }
    
    // Then sort by title
    const titleA = TITLES.indexOf(a.title);
    const titleB = TITLES.indexOf(b.title);
    
    return titleA - titleB;
  });
};

export default function Dashboard({ onLogout, userRole, userPermissions, userChapter, userTitle }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false);
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [memberFiles, setMemberFiles] = useState([]);
  const [memberFilesLoading, setMemberFilesLoading] = useState(false);
  const [newFileCategory, setNewFileCategory] = useState("club_forms");
  const [newFileLabel, setNewFileLabel] = useState("");
  const [newFileObj, setNewFileObj] = useState(null);
  const [newFileUploading, setNewFileUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteReasonCategory, setDeleteReasonCategory] = useState("");
  const [kickFromDiscord, setKickFromDiscord] = useState(false);
  const [cancelSquareSubscription, setCancelSquareSubscription] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [questionnaireStatus, setQuestionnaireStatus] = useState({});
  const [bulkQDialogOpen, setBulkQDialogOpen] = useState(false);
  const [bulkQSelected, setBulkQSelected] = useState([]);
  const [bulkQSearch, setBulkQSearch] = useState("");
  const [bulkQSending, setBulkQSending] = useState(false);
  const [actionForm, setActionForm] = useState({ type: "merit", date: "", description: "" });
  const [editingAction, setEditingAction] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("All");
  const { active: activeChapters, inactive: inactiveChapters } = useChapters();
  const [meetingDates, setMeetingDates] = useState([]);
  const [unreadPrivateCount, setUnreadPrivateCount] = useState(0);
  const [upcomingEventsCount, setUpcomingEventsCount] = useState(0);
  const [totalExperience, setTotalExperience] = useState(null);
  const [attendanceExpanded, setAttendanceExpanded] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear().toString()]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [duesExpanded, setDuesExpanded] = useState(false);
  const [addMeetingDialogOpen, setAddMeetingDialogOpen] = useState(false);
  const [newMeetingDate, setNewMeetingDate] = useState("");
  const [newMeetingStatus, setNewMeetingStatus] = useState(1);
  const [newMeetingNote, setNewMeetingNote] = useState("");
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [myDues, setMyDues] = useState(null);
  const [myDuesLoading, setMyDuesLoading] = useState(true);
  const [myDuesExpanded, setMyDuesExpanded] = useState(false);

  // My Financial Assistance (members can see their own ledger entries)
  const [myAssistance, setMyAssistance] = useState(null);
  const [myAssistanceExpanded, setMyAssistanceExpanded] = useState(false);

  // My Quizzes (pending quizzes the logged-in member is eligible for)
  const [myQuizzes, setMyQuizzes] = useState([]);
  
  // Document Signing State (In-house system)
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [memberDocuments, setMemberDocuments] = useState([]);
  const [sendDocDialogOpen, setSendDocDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [docMessage, setDocMessage] = useState("");
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [nationalOfficers, setNationalOfficers] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [viewDocDetailsOpen, setViewDocDetailsOpen] = useState(false);
  const [selectedDocDetails, setSelectedDocDetails] = useState(null);
  
  const navigate = useNavigate();

  const titleLists = useOfficerTitles();

  // Check if user is National Admin
  const isNationalAdmin = userRole === 'admin' && userChapter === 'National';
  
  // National officer titles that can perform actions on National members
  const NATIONAL_OFFICER_TITLES = titleLists.national_officer_titles;
  
  // Check if current user is a National officer who can act on National members
  const isNationalOfficer = userChapter === 'National' && NATIONAL_OFFICER_TITLES.includes(userTitle);
  
  // PM (Prospect Master) has read-only access - no action buttons
  const isPM = userTitle === 'PM';
  
  // Check if user can edit a specific member (based on chapter and title restrictions)
  const canEditMember = (memberChapter) => {
    if (userRole !== 'admin') return false;
    
    // PM has read-only access - cannot edit any members
    if (isPM) return false;
    
    // For National chapter members, only specific National officers can edit
    if (memberChapter === 'National') {
      return isNationalOfficer;
    }
    
    // National officers can edit any non-National chapter
    if (userChapter === 'National') return true;
    
    // Chapter admins can only edit their own chapter
    return userChapter === memberChapter;
  };

  // Check if user can access prospects (National, HA Admin, or PM)
  const canAccessProspects = userPermissions?.view_prospects;

  // Helper to check permissions - NO admin bypass, all access controlled via Permission Panel
  const hasPermission = (permission) => {
    return userPermissions?.[permission] === true;
  };

  // Check if a member is an officer (has a title that's not Brother or Honorary)
  const isOfficer = (member) => {
    const officerTitles = titleLists.officer_titles;
    return officerTitles.includes(member?.title);
  };

  // Check if current user is a chapter officer (AD, HA, HS, or National)
  const isCurrentUserOfficer = () => {
    const officerTitles = titleLists.officer_titles;
    return officerTitles.includes(userTitle);
  };

  // Check if user can see email for a specific member
  // - Admins and users with email permission can see all
  // - Chapter officers (AD, HA, HS, National) can see all officers' emails
  // - Regular members can see email of officers in their own chapter
  const canSeeEmail = (member) => {
    if (hasPermission('email')) return true;
    // Chapter officers can see all officers' emails
    if (isCurrentUserOfficer() && isOfficer(member)) return true;
    // Regular members can see their chapter officers' emails
    if (member?.chapter === userChapter && isOfficer(member)) return true;
    return false;
  };

  // Check if user can see phone for a specific member
  // - Admins and users with phone permission can see all (respecting private)
  // - Chapter officers can see all officers' phones (unless private)
  // - Regular members can see their chapter officers' phones (unless private)
  const canSeePhone = (member) => {
    // If phone is marked private, only admins can see
    if (member?.phone_private) {
      return hasPermission('phone');
    }
    if (hasPermission('phone')) return true;
    // Chapter officers can see all officers' phones
    if (isCurrentUserOfficer() && isOfficer(member)) return true;
    // Regular members can see their chapter officers' phones
    if (member?.chapter === userChapter && isOfficer(member)) return true;
    return false;
  };

  // Check if user can see personal email for a specific member
  // - If personal_email_private is true, only users with view_private_personal_email can see
  // - Otherwise, visible to all users who can see the member
  const canSeePersonalEmail = (member) => {
    if (!member?.personal_email) return false;
    if (member?.personal_email_private) {
      return hasPermission('view_private_personal_email');
    }
    return true;
  };

  // Fetch available years (admin only)
  useEffect(() => {
    if (userRole === 'admin') {
      const token = localStorage.getItem('token');
      axios.get(`${API}/admin/available-years`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setAvailableYears(res.data.years);
      }).catch(err => {
        console.error("Failed to fetch available years:", err);
      });
    }
  }, [userRole]);

  // Email groups shared with this user (controls menu visibility for non-managers)
  const [hasSharedEmailGroups, setHasSharedEmailGroups] = useState(false);
  useEffect(() => {
    if (userPermissions?.manage_email_groups) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/email-groups?light=true`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setHasSharedEmailGroups((res.data.groups || []).length > 0);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Generic error handler for API calls
  const handleApiError = (error, context) => {
    console.error(`${context}:`, error);
    
    // Handle token expiration
    if (error.response?.status === 401) {
      toast.error("Session expired. Please log in again.");
      setTimeout(() => {
        onLogout();
      }, 1500);
      return true; // Handled
    }
    
    return false; // Not handled
  };


  const [selectedDuesYear, setSelectedDuesYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    chapter: "",
    title: "",
    handle: "",
    name: "",
    email: "",
    personal_email: "",
    phone: "",
    address: "",
    dob: "",
    join_date: "",
    experience_start: "",
    phone_private: false,
    address_private: false,
    email_private: false,
    personal_email_private: false,
    name_private: false,
    military_service: false,
    military_branch: "",
    is_first_responder: false,
    non_dues_paying: false,
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
    emergency_contact_private: false,
    dues: {
      [new Date().getFullYear().toString()]: Array(12).fill(false)
    },
    meeting_attendance: {
      [new Date().getFullYear().toString()]: []  // Flexible array of meetings
    }
  });

  useEffect(() => {
    fetchMembers();
    fetchUnreadPrivateCount();
    fetchUpcomingEventsCount();
    fetchTotalExperience();
    fetchQuestionnaireStatus();
    fetchMyDues();
    fetchMyAssistance();
    fetchMyQuizzes();
    // Auto-refresh counts every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadPrivateCount();
      fetchUpcomingEventsCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [userRole]);

  const fetchMyDues = async () => {
    try {
      setMyDuesLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/my-dues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyDues(response.data);
    } catch (error) {
      console.error("Failed to fetch my dues:", error);
    } finally {
      setMyDuesLoading(false);
    }
  };

  const fetchMyAssistance = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/financial-assistance/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyAssistance(response.data);
    } catch (error) {
      console.error("Failed to fetch my financial assistance:", error);
    }
  };

  const fetchMyQuizzes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/quizzes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Only show "to-do" quizzes — admins/managers get the full manager via the menu
      if (response.data?.can_manage) {
        setMyQuizzes([]);
        return;
      }
      const list = (response.data?.quizzes || []).filter(
        (q) => !q.attempt?.passed
      );
      setMyQuizzes(list);
    } catch (error) {
      // Quietly ignore
    }
  };

  const fetchTotalExperience = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/stats/experience`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTotalExperience(response.data);
    } catch (error) {
      console.error("Failed to fetch total experience:", error);
    }
  };

  const fetchUnreadPrivateCount = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/messages/unread/count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadPrivateCount(response.data.unread_count);
    } catch (error) {
      if (!handleApiError(error, "Failed to fetch unread private messages count")) {
        console.error("Failed to fetch unread private messages count:", error);
      }
    }
  };

  const fetchUpcomingEventsCount = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/events/upcoming-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUpcomingEventsCount(response.data.count);
    } catch (error) {
      // Silently fail - events feature might not be critical
      console.error("Failed to fetch upcoming events count:", error);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError("");
    setPasswordSuccess("");
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPasswordSuccess("Password changed successfully!");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => {
        setPasswordDialogOpen(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || "Failed to change password");
    }
  };


  // Update meeting dates whenever the year changes
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const dates = getMeetingDates(currentYear);
    setMeetingDates(dates);
  }, [formData.meeting_attendance.year]);

  const fetchQuestionnaireStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/questionnaire-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuestionnaireStatus(response.data || {});
    } catch (error) {
      // Non-critical badge data — fail silently
    }
  };

  const isPendingQuestionnaire = (member) =>
    questionnaireStatus[member.id] === "pending" || questionnaireStatus[member.id] === "expired";

  const handleResendQuestionnaire = async (member) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${API}/members/${member.id}/questionnaire/send`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.dm_sent || response.data?.email_sent) {
        const ch = [];
        if (response.data?.dm_sent) ch.push("Discord DM");
        if (response.data?.email_sent) ch.push("email");
        toast.success(`Questionnaire link sent to ${member.handle} via ${ch.join(" + ")} (expires in 72 hours)`);
      } else if (response.data?.link) {
        try { await navigator.clipboard.writeText(response.data.link); } catch (e) { /* clipboard unavailable */ }
        toast.warning(
          `DM and email both failed (${response.data?.dm_error || "no Discord"} / ${response.data?.email_error || "no email"}). Fresh link copied to clipboard — share it manually.`,
          { duration: 8000 }
        );
      }
      fetchQuestionnaireStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send questionnaire");
    }
  };

  const handleBulkSendQuestionnaires = async () => {
    if (bulkQSelected.length === 0) {
      toast.error("Select at least one member");
      return;
    }
    setBulkQSending(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${API}/questionnaire/bulk-send`,
        { member_ids: bulkQSelected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = response.data || {};
      if (d.sent > 0) toast.success(`Questionnaire links sent to ${d.sent} member(s) — ${d.dm_count || 0} Discord DMs, ${d.email_count || 0} emails`);
      if (d.skipped_recent?.length > 0) {
        toast.info(`Skipped ${d.skipped_recent.length} (completed within the last year): ${d.skipped_recent.join(", ")}`, { duration: 8000 });
      }
      if (d.skipped_no_discord?.length > 0) {
        toast.warning(`Skipped ${d.skipped_no_discord.length} (no Discord or email on file): ${d.skipped_no_discord.join(", ")}`, { duration: 8000 });
      }
      if (d.failed?.length > 0) {
        toast.error(`Delivery failed for ${d.failed.length}: ${d.failed.join(", ")} — use the resend button on their row to copy a link`, { duration: 10000 });
      }
      setBulkQDialogOpen(false);
      setBulkQSelected([]);
      fetchQuestionnaireStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send questionnaires");
    } finally {
      setBulkQSending(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem("token");
      console.log("Fetching members with token:", token ? token.substring(0, 20) + "..." : "NO TOKEN");
      const response = await axios.get(`${API}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Ensure response.data is an array
      const membersArray = Array.isArray(response.data) ? response.data : [];
      console.log("Members loaded successfully:", membersArray.length, "members");
      const sortedMembers = sortMembers(membersArray);
      setMembers(sortedMembers);
    } catch (error) {
      if (handleApiError(error, "Failed to load members")) {
        return;
      }
      toast.error(`Failed to load members: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      if (editingMember) {
        await axios.put(`${API}/members/${editingMember.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Member updated successfully");
      } else {
        await axios.post(`${API}/members`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Member added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = (member) => {
    setMemberToDelete(member);
    setDeleteReason("");
    setKickFromDiscord(false);
    setCancelSquareSubscription(true);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteReasonCategory) {
      toast.error("Please select a reason category");
      return;
    }
    if (deleteReasonCategory === 'other' && !deleteReason.trim()) {
      toast.error("Please provide details for 'Other' reason");
      return;
    }

    // Map category to display text for the reason
    const categoryLabels = {
      resigned: "Resigned by Email Notification",
      dues_nonpayment: "Not Paying Dues Over 30 Days",
      member_vote: "Removed by Member Vote",
      national_board: "Removed by National Board Vote",
      inactive: "Inactive / No Contact",
      deceased: "Deceased",
      other: "Other"
    };
    
    // Combine category and details into full reason for database record
    const fullReason = deleteReason.trim() 
      ? `${categoryLabels[deleteReasonCategory]}: ${deleteReason.trim()}`
      : categoryLabels[deleteReasonCategory];

    const token = localStorage.getItem("token");
    try {
      const response = await axios.delete(`${API}/members/${memberToDelete.id}`, {
        params: { 
          reason: fullReason,
          reason_category: deleteReasonCategory,
          additional_details: deleteReason.trim() || null,
          kick_from_discord: kickFromDiscord,
          cancel_square_subscription: cancelSquareSubscription
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Build success message based on what was done
      let successMsg = "Member archived successfully";
      const actions = [];
      
      if (kickFromDiscord && response.data.discord_result) {
        if (response.data.discord_result.success) {
          actions.push("kicked from Discord");
        } else {
          actions.push("Discord kick failed");
        }
      }
      
      if (response.data.square_subscription_cancelled) {
        actions.push("Square subscription cancelled");
      } else if (response.data.square_result?.had_subscription && !response.data.square_subscription_cancelled) {
        actions.push("Square cancellation failed");
      }
      
      if (actions.length > 0) {
        successMsg = `Member archived (${actions.join(", ")})`;
      }
      
      toast.success(successMsg);
      
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      setDeleteReason("");
      setDeleteReasonCategory("");
      setKickFromDiscord(false);
      setCancelSquareSubscription(true);
      fetchMembers();
    } catch (error) {
      toast.error("Failed to archive member");
    }
  };

  const handleOpenActions = (member) => {
    setSelectedMember(member);
    setActionForm({ type: "merit", date: new Date().toISOString().split('T')[0], description: "" });
    setEditingAction(null); // Reset editing state
    setActionsDialogOpen(true);
  };

  // ─── Member Files (Feb 2026 — officer-curated document attachments) ───
  const fetchMemberFiles = async (memberId) => {
    setMemberFilesLoading(true);
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(`${API}/members/${memberId}/files`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMemberFiles(res.data?.files || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load files");
      setMemberFiles([]);
    } finally {
      setMemberFilesLoading(false);
    }
  };

  const handleOpenMemberFiles = async (member) => {
    setSelectedMember(member);
    setNewFileCategory("club_forms");
    setNewFileLabel("");
    setNewFileObj(null);
    setFilesDialogOpen(true);
    await fetchMemberFiles(member.id);
  };

  const handleUploadMemberFile = async () => {
    if (!selectedMember || !newFileObj) {
      toast.error("Pick a file first");
      return;
    }
    const fd = new FormData();
    fd.append("file", newFileObj);
    fd.append("category", newFileCategory);
    if (newFileLabel.trim()) fd.append("label", newFileLabel.trim());
    setNewFileUploading(true);
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${API}/members/${selectedMember.id}/files`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 120000,
      });
      toast.success("File uploaded");
      setNewFileObj(null);
      setNewFileLabel("");
      setNewFileCategory("club_forms");
      await fetchMemberFiles(selectedMember.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setNewFileUploading(false);
    }
  };

  const handleDownloadMemberFile = async (file) => {
    if (!selectedMember) return;
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(
        `${API}/members/${selectedMember.id}/files/${file.id}/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename || file.label || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const handleViewMemberFile = async (file) => {
    if (!selectedMember) return;
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(
        `${API}/members/${selectedMember.id}/files/${file.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { disposition: "inline" },
          responseType: "blob",
        }
      );
      // Force the correct mime so the browser previews PDFs/images inline.
      const blob = new Blob([res.data], {
        type: file.content_type || res.data.type || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.error("Browser blocked the popup — allow popups for this site to view inline");
      }
      // Revoke after a delay so the new tab has time to load
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open file");
    }
  };

  const handleDeleteMemberFile = async (file) => {
    if (!selectedMember) return;
    const confirmMessage =
      `Permanently delete "${file.label || file.original_filename}" from ${selectedMember.handle}'s profile?\n\n` +
      `This cannot be undone. The action will be logged under your username.`;
    if (!window.confirm(confirmMessage)) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(
        `${API}/members/${selectedMember.id}/files/${file.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("File deleted");
      await fetchMemberFiles(selectedMember.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Document Signing Functions (In-house system)
  const fetchDocumentTemplates = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/documents/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentTemplates(response.data || []);
    } catch (error) {
      console.log("Documents not available or no permission");
      setDocumentTemplates([]);
    }
  };

  const fetchNationalOfficers = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/documents/national-officers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNationalOfficers(response.data || []);
    } catch (error) {
      console.log("Error fetching national officers:", error);
      setNationalOfficers([]);
    }
  };

  const fetchMemberDocuments = async (memberId) => {
    const token = localStorage.getItem("token");
    setLoadingDocuments(true);
    try {
      const response = await axios.get(`${API}/documents/requests`, {
        params: { member_id: memberId },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemberDocuments(response.data || []);
    } catch (error) {
      console.log("Error fetching documents:", error);
      setMemberDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleOpenDocuments = async (member) => {
    setSelectedMember(member);
    setDocumentsDialogOpen(true);
    await Promise.all([
      fetchDocumentTemplates(),
      fetchNationalOfficers(),
      fetchMemberDocuments(member.id)
    ]);
  };

  const handleSendDocument = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a document template");
      return;
    }
    if (!selectedEmail) {
      toast.error("Please select an email address");
      return;
    }
    
    const token = localStorage.getItem("token");
    try {
      const formData = new FormData();
      formData.append('template_id', selectedTemplate);
      formData.append('member_id', selectedMember.id);
      formData.append('recipient_email', selectedEmail);
      formData.append('recipient_name', selectedMember.handle || selectedMember.first_name);
      formData.append('message', docMessage || '');
      
      // Add approvers if selected
      if (selectedApprovers.length > 0) {
        formData.append('approvers', JSON.stringify(selectedApprovers));
      }
      
      await axios.post(`${API}/documents/send`, formData, { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        } 
      });
      
      toast.success("Document sent successfully" + (selectedApprovers.length > 0 ? ` with ${selectedApprovers.length} approver(s)` : ""));
      setSendDocDialogOpen(false);
      setSelectedTemplate("");
      setSelectedEmail("");
      setDocMessage("");
      setSelectedApprovers([]);
      fetchMemberDocuments(selectedMember.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send document");
    }
  };

  const toggleApprover = (role) => {
    setSelectedApprovers(prev => {
      if (prev.includes(role)) {
        return prev.filter(r => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  const moveApproverUp = (index) => {
    if (index === 0) return;
    setSelectedApprovers(prev => {
      const newArr = [...prev];
      [newArr[index - 1], newArr[index]] = [newArr[index], newArr[index - 1]];
      return newArr;
    });
  };

  const moveApproverDown = (index) => {
    if (index === selectedApprovers.length - 1) return;
    setSelectedApprovers(prev => {
      const newArr = [...prev];
      [newArr[index], newArr[index + 1]] = [newArr[index + 1], newArr[index]];
      return newArr;
    });
  };

  const handleViewDocDetails = (doc) => {
    setSelectedDocDetails(doc);
    setViewDocDetailsOpen(true);
  };

  const handleDownloadSignedDoc = async (requestId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/documents/signed/${requestId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `signed_document_${requestId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Document downloaded");
    } catch (error) {
      // Handle blob response errors - need to read the blob as text
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          toast.error(json.detail || "Failed to download document");
        } catch {
          toast.error("Failed to download document");
        }
      } else {
        toast.error(error.response?.data?.detail || "Failed to download document");
      }
    }
  };

  const handleDeleteDocumentRecord = async (docId, currentStatus) => {
    // For cancelled/completed/expired/denied documents, offer permanent deletion
    const isFinalState = ["cancelled", "completed", "expired", "denied"].includes(currentStatus);
    
    const confirmMessage = isFinalState
      ? "Are you sure you want to permanently delete this document record? This cannot be undone."
      : "Are you sure you want to cancel this document request?";
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    const token = localStorage.getItem("token");
    try {
      // Add permanent=true for documents in final states
      const url = isFinalState 
        ? `${API}/documents/requests/${docId}?permanent=true`
        : `${API}/documents/requests/${docId}`;
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isFinalState ? "Document permanently deleted" : "Document request cancelled");
      fetchMemberDocuments(selectedMember.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process document request");
    }
  };

  const getDocumentStatusBadge = (doc) => {
    const status = doc.status;
    const statusMap = {
      sent: { label: "Pending", className: "bg-yellow-600 text-yellow-100" },
      pending: { label: "Pending", className: "bg-yellow-600 text-yellow-100" },
      pending_recipient: { label: "Awaiting Signature", className: "bg-yellow-600 text-yellow-100" },
      viewed: { label: "Viewed", className: "bg-blue-600 text-blue-100" },
      pending_approval: { label: "Awaiting Approval", className: "bg-orange-600 text-orange-100" },
      completed: { label: doc.final_decision === "denied" ? "Denied" : "Approved", className: doc.final_decision === "denied" ? "bg-red-600 text-red-100" : "bg-green-600 text-green-100" },
      signed: { label: "Signed", className: "bg-green-600 text-green-100" },
      declined: { label: "Declined", className: "bg-red-600 text-red-100" },
      cancelled: { label: "Cancelled", className: "bg-gray-600 text-gray-100" },
      expired: { label: "Expired", className: "bg-gray-600 text-gray-100" },
      unknown: { label: "Unknown", className: "bg-slate-600 text-slate-100" }
    };
    const info = statusMap[status] || { label: status, className: "bg-slate-600 text-slate-100" };
    return <span className={`px-2 py-1 rounded text-xs ${info.className}`}>{info.label}</span>;
  };

  const handleAddAction = async (e) => {
    e.preventDefault();
    if (!actionForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      await axios.post(
        `${API}/members/${selectedMember.id}/actions`,
        null,
        {
          params: {
            action_type: actionForm.type,
            date: actionForm.date,
            description: actionForm.description
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Action added successfully");
      setActionForm({ type: "merit", date: new Date().toISOString().split('T')[0], description: "" });
      fetchMembers(); // Refresh to get updated actions
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add action");
    }
  };

  const handleDeleteAction = async (actionId) => {
    if (!window.confirm("Are you sure you want to delete this action?")) return;

    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/members/${selectedMember.id}/actions/${actionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Action deleted successfully");
      fetchMembers(); // Refresh to get updated actions
    } catch (error) {
      toast.error("Failed to delete action");
    }
  };

  const handleEditAction = (action) => {
    setEditingAction(action);
    setActionForm({
      type: action.type,
      date: action.date,
      description: action.description
    });
  };

  const handleUpdateAction = async (e) => {
    e.preventDefault();
    if (!actionForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      await axios.put(
        `${API}/members/${selectedMember.id}/actions/${editingAction.id}`,
        {
          action_type: actionForm.type,
          date: actionForm.date,
          description: actionForm.description
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Action updated successfully");
      setEditingAction(null);
      setActionForm({ type: "merit", date: new Date().toISOString().split('T')[0], description: "" });
      fetchMembers(); // Refresh to get updated actions
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update action");
    }
  };

  const handleCancelEdit = () => {
    setEditingAction(null);
    setActionForm({ type: "merit", date: new Date().toISOString().split('T')[0], description: "" });
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    
    // Handle meeting_attendance - support both old and new format
    let attendanceData = {};
    const currentYear = new Date().getFullYear().toString();
    
    if (member.meeting_attendance) {
      // Check if it's new format (keys are years with arrays) or old format (has 'year' key)
      if (member.meeting_attendance.year && member.meeting_attendance.meetings) {
        // Old format - convert to new flexible format
        const yearStr = member.meeting_attendance.year.toString();
        const meetings = member.meeting_attendance.meetings || [];
        // Convert old indexed meetings to dated meetings (approximate dates)
        attendanceData[yearStr] = meetings.map((m, idx) => {
          const monthIdx = Math.floor(idx / 2);
          const weekNum = (idx % 2) + 1;
          const approxDate = new Date(parseInt(yearStr), monthIdx, weekNum * 7);
          return {
            date: approxDate.toISOString().split('T')[0],
            status: typeof m === 'object' ? (m.status || 0) : (m || 0),
            note: typeof m === 'object' ? (m.note || '') : ''
          };
        }).filter(m => m.status !== 0 || m.note); // Only keep non-empty meetings
      } else {
        // New format - use as is
        attendanceData = { ...member.meeting_attendance };
      }
    }
    
    // Ensure current year exists
    if (!attendanceData[currentYear]) {
      attendanceData[currentYear] = [];
    }
    
    // Handle dues - support both old and new format
    let duesData = {};
    if (member.dues) {
      if (member.dues.year) {
        // Old format - convert
        const yearStr = member.dues.year.toString();
        duesData[yearStr] = member.dues.months || Array(12).fill(false);
      } else {
        // New format - use as is
        duesData = { ...member.dues };
        if (!duesData[currentYear]) {
          duesData[currentYear] = Array(12).fill(false);
        }
      }
    } else {
      duesData[currentYear] = Array(12).fill(false);
    }
    
    setFormData({
      chapter: member.chapter,
      title: member.title,
      handle: member.handle,
      name: member.name,
      email: member.email,
      personal_email: member.personal_email || "",
      phone: member.phone,
      address: member.address,
      dob: member.dob || "",
      join_date: member.join_date || "",
      experience_start: member.experience_start || "",
      phone_private: member.phone_private || false,
      address_private: member.address_private || false,
      email_private: member.email_private || false,
      personal_email_private: member.personal_email_private || false,
      name_private: member.name_private || false,
      military_service: member.military_service || false,
      military_branch: member.military_branch || "",
      is_first_responder: member.is_first_responder || false,
      non_dues_paying: member.non_dues_paying || false,
      emergency_contact_name: member.emergency_contact_name || "",
      emergency_contact_phone: member.emergency_contact_phone || "",
      emergency_contact_relationship: member.emergency_contact_relationship || "",
      emergency_contact_private: member.emergency_contact_private || false,
      dues: duesData,
      meeting_attendance: attendanceData
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    const currentYear = new Date().getFullYear().toString();
    setFormData({
      chapter: "",
      title: "",
      handle: "",
      name: "",
      email: "",
      personal_email: "",
      phone: "",
      address: "",
      dob: "",
      join_date: "",
      experience_start: "",
      phone_private: false,
      address_private: false,
      email_private: false,
      personal_email_private: false,
      name_private: false,
      military_service: false,
      military_branch: "",
      is_first_responder: false,
      non_dues_paying: false,
      dues: {
        [currentYear]: Array(12).fill(false)
      },
      meeting_attendance: {
        [currentYear]: []  // Empty flexible array
      }
    });
    setEditingMember(null);
  };

  const handleDuesToggle = (monthIndex) => {
    const currentYear = selectedDuesYear.toString();
    const yearMonths = formData.dues[currentYear] || Array(12).fill(null).map(() => ({ status: "unpaid", note: "" }));
    const currentStatus = yearMonths[monthIndex]?.status || "unpaid";
    
    // Cycle through: unpaid -> paid -> late -> unpaid
    let newStatus = "unpaid";
    if (currentStatus === "unpaid") newStatus = "paid";
    else if (currentStatus === "paid") newStatus = "late";
    else newStatus = "unpaid";
    
    const newMonths = [...yearMonths];
    newMonths[monthIndex] = {
      status: newStatus,
      note: yearMonths[monthIndex]?.note || ""
    };
    
    setFormData({
      ...formData,
      dues: {
        ...formData.dues,
        [currentYear]: newMonths
      }
    });
  };

  const handleDuesNoteChange = (monthIndex, note) => {
    const currentYear = selectedDuesYear.toString();
    const yearMonths = formData.dues[currentYear] || Array(12).fill(null).map(() => ({ status: "unpaid", note: "" }));
    const newMonths = [...yearMonths];
    newMonths[monthIndex] = {
      ...newMonths[monthIndex],
      note: note
    };
    
    setFormData({
      ...formData,
      dues: {
        ...formData.dues,
        [currentYear]: newMonths
      }
    });
  };

  const handleDuesYearChange = (newYear) => {
    setSelectedDuesYear(newYear);
    // Create year entry if doesn't exist
    if (!formData.dues[newYear.toString()]) {
      setFormData({
        ...formData,
        dues: {
          ...formData.dues,
          [newYear.toString()]: Array(12).fill(false)
        }
      });
    }
  };

  const handleAttendanceToggle = (meetingIndex) => {
    const currentYear = new Date().getFullYear().toString();
    const yearMeetings = formData.meeting_attendance[currentYear] || [];
    const newMeetings = [...yearMeetings];
    
    // Cycle through states: 0 (Absent) -> 1 (Present) -> 2 (Excused) -> 0
    const currentStatus = newMeetings[meetingIndex]?.status || 0;
    newMeetings[meetingIndex] = {
      ...newMeetings[meetingIndex],
      status: (currentStatus + 1) % 3
    };
    
    setFormData({
      ...formData,
      meeting_attendance: {
        ...formData.meeting_attendance,
        [currentYear]: newMeetings
      }
    });
  };

  const handleAttendanceNote = (meetingIndex, note) => {
    const currentYear = new Date().getFullYear().toString();
    const yearMeetings = formData.meeting_attendance[currentYear] || [];
    const newMeetings = [...yearMeetings];
    
    newMeetings[meetingIndex] = {
      ...newMeetings[meetingIndex],
      note: note
    };
    
    setFormData({
      ...formData,
      meeting_attendance: {
        ...formData.meeting_attendance,
        [currentYear]: newMeetings
      }
    });
  };

  // Add a new meeting to the member's attendance
  const handleAddMeeting = () => {
    if (!newMeetingDate) {
      toast.error("Please select a date");
      return;
    }
    
    const meetingYear = newMeetingDate.split('-')[0];
    const yearMeetings = formData.meeting_attendance[meetingYear] || [];
    
    // Check if meeting already exists for this date
    if (yearMeetings.some(m => m.date === newMeetingDate)) {
      toast.error("A meeting already exists for this date");
      return;
    }
    
    const newMeeting = {
      date: newMeetingDate,
      status: newMeetingStatus,
      note: newMeetingNote
    };
    
    const newMeetings = [...yearMeetings, newMeeting].sort((a, b) => a.date.localeCompare(b.date));
    
    setFormData({
      ...formData,
      meeting_attendance: {
        ...formData.meeting_attendance,
        [meetingYear]: newMeetings
      }
    });
    
    // Reset form
    setNewMeetingDate("");
    setNewMeetingStatus(1);
    setNewMeetingNote("");
    setAddMeetingDialogOpen(false);
    toast.success("Meeting added");
  };

  // Delete a meeting from the member's attendance (syncs to A & D)
  const handleDeleteMeeting = async (meetingIndex) => {
    const currentYear = selectedYear;
    const yearMeetings = formData.meeting_attendance[currentYear] || [];
    const meetingToDelete = yearMeetings[meetingIndex];
    
    // Delete from server (syncs to officer_attendance collection)
    if (meetingToDelete && meetingToDelete.date && formData.id) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${API}/members/${formData.id}/attendance?meeting_date=${meetingToDelete.date}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Failed to sync attendance deletion:", error);
        // Continue with local update even if server sync fails
      }
    }
    
    const newMeetings = yearMeetings.filter((_, idx) => idx !== meetingIndex);
    
    setFormData({
      ...formData,
      meeting_attendance: {
        ...formData.meeting_attendance,
        [currentYear]: newMeetings
      }
    });
    toast.success("Meeting removed");
  };

  // Format date for display
  const formatMeetingDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const handleExportCSV = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/members/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "members.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV downloaded successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const handleViewCSV = () => {
    // Navigate to the CSV export view page
    window.open('/export-view', '_blank');
    toast.success("Opening CSV export view...");
  };

  // Filter by chapter first, then by search term
  const filteredMembers = members.filter((member) => {
    if (!member) return false;
    // Chapter filter
    if (selectedChapter !== "All" && member.chapter !== selectedChapter) {
      return false;
    }
    // Search filter
    const search = searchTerm.toLowerCase();
    return (
      (member.name || '').toLowerCase().includes(search) ||
      (member.handle || '').toLowerCase().includes(search) ||
      (member.email || '').toLowerCase().includes(search) ||
      (member.chapter || '').toLowerCase().includes(search)
    );
  });

  // Get member counts by chapter
  const chapterCounts = { All: members.length };
  [...activeChapters, ...inactiveChapters].forEach((c) => {
    chapterCounts[c] = members.filter((m) => m.chapter === c).length;
  });

  // Sort filtered members to maintain order
  const sortedFilteredMembers = sortMembers([...filteredMembers]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <nav className="bg-slate-800 border-b border-slate-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              {/* Navigation Dropdown */}
              <DropdownMenu modal={true}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="nav-menu-trigger" className="flex items-center gap-2 bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600">
                    <Menu className="w-4 h-4" />
                    <span className="hidden sm:inline">Menu</span>
                    {(unreadPrivateCount > 0 || upcomingEventsCount > 0) && (
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 bg-slate-800 border-slate-700 max-h-[80vh] overflow-y-auto" sideOffset={8}>
                  {/* === MEMBER MANAGEMENT === */}
                  {canAccessProspects && (
                    <DropdownMenuItem 
                      onSelect={(e) => { e.preventDefault(); navigate("/prospects"); }} 
                      className="text-slate-200 focus:bg-slate-700 focus:text-white cursor-pointer"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Prospects
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuSeparator className="bg-slate-700" />
                  
                  {/* === COMMUNICATION === */}
                  <DropdownMenuItem 
                    onSelect={(e) => { e.preventDefault(); navigate("/events"); }} 
                    className="text-slate-200 focus:bg-slate-700 focus:text-white cursor-pointer"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Events
                    {upcomingEventsCount > 0 && (
                      <span className="ml-auto bg-green-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5">
                        {upcomingEventsCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  {(userRole === 'admin' || userPermissions?.access_polls || userPermissions?.manage_polls) && (
                  <DropdownMenuItem 
                    onSelect={(e) => { e.preventDefault(); navigate("/polls"); }} 
                    className="text-slate-200 focus:bg-slate-700 focus:text-white cursor-pointer"
                    data-testid="menu-voting-polls"
                  >
                    <Vote className="w-4 h-4 mr-2" />
                    Voting Polls
                  </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onSelect={(e) => { e.preventDefault(); navigate("/messages"); }} 
                    className="text-slate-200 focus:bg-slate-700 focus:text-white cursor-pointer"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Messages
                    {unreadPrivateCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5">
                        {unreadPrivateCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator className="bg-slate-700" />
                  
                  {/* === CLUB FEATURES === */}
                  <DropdownMenuItem 
                    onSelect={(e) => { e.preventDefault(); navigate("/store"); }} 
                    className="text-green-400 focus:bg-green-900/30 focus:text-green-300 cursor-pointer"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Store
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onSelect={(e) => { e.preventDefault(); navigate("/wall-of-honor"); }} 
                    className="text-amber-400 focus:bg-amber-900/30 focus:text-amber-300 cursor-pointer"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Wall of Honor
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onSelect={(e) => { e.preventDefault(); navigate("/suggestions"); }} 
                    className="text-yellow-400 focus:bg-yellow-900/30 focus:text-yellow-300 cursor-pointer"
                  >
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Suggestion Box
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onSelect={(e) => { e.preventDefault(); navigate("/forms"); }} 
                    className="text-blue-400 focus:bg-blue-900/30 focus:text-blue-300 cursor-pointer"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Forms
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onSelect={(e) => { e.preventDefault(); navigate("/downloads"); }} 
                    className="text-cyan-400 focus:bg-cyan-900/30 focus:text-cyan-300 cursor-pointer"
                    data-testid="nav-downloads"
                  >
                    <HardDrive className="w-4 h-4 mr-2" />
                    Downloads
                  </DropdownMenuItem>
                  
                  {/* === ADMIN SECTION (Only visible to users with admin permissions) === */}
                  {(userPermissions?.ad_page_access || userPermissions?.manage_dues_reminders || userPermissions?.manage_system_users || userPermissions?.view_reports || userPermissions?.manage_events || userPermissions?.manage_polls || userPermissions?.manage_quizzes || userPermissions?.manage_qr_codes || ["Prez", "VP", "S@A", "Enf", "ENF", "CTO", "NPrez", "NVP", "NS@A", "NENF", "NSEC"].includes(userTitle)) && (
                    <>
                      <DropdownMenuSeparator className="bg-slate-700" />
                      <div className="px-2 py-1.5 text-xs font-semibold text-red-400 uppercase tracking-wider">
                        Admin
                      </div>
                      
                      {/* --- MEMBERS & ORG GROUP --- */}
                      {(userPermissions?.ad_page_access || userPermissions?.manage_events || userPermissions?.manage_quizzes || (userChapter === 'National' && titleLists.promotion_eligible_titles.includes(userTitle))) && (
                        <>
                          <div className="px-2 py-1 text-[10px] font-medium text-slate-500 uppercase tracking-wider mt-1">
                            Members & Org
                          </div>
                          
                          {/* A & D (Attendance & Dues) */}
                          {userPermissions?.ad_page_access && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/officer-tracking"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <Users className="w-4 h-4 mr-2" />
                              A & D
                            </DropdownMenuItem>
                          )}

                          {/* Quizzes (manage_quizzes permission) */}
                          {userPermissions?.manage_quizzes && (
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); navigate("/quizzes"); }}
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="nav-quizzes"
                            >
                              <ClipboardList className="w-4 h-4 mr-2" />
                              Quizzes
                            </DropdownMenuItem>
                          )}
                          
                          {/* Promotions - National officers only */}
                          {userChapter === 'National' && titleLists.promotion_eligible_titles.includes(userTitle) && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/promotions"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <Star className="w-4 h-4 mr-2" />
                              Promotions
                            </DropdownMenuItem>
                          )}
                          
                          {/* Manage Events */}
                          {userPermissions?.manage_events && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/events"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <Calendar className="w-4 h-4 mr-2" />
                              Events
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                      
                      {/* --- DOCUMENTS GROUP --- */}
                      {(userPermissions?.view_reports || userPermissions?.send_documents || userRole === 'admin') && (
                        <>
                          <div className="px-2 py-1 text-[10px] font-medium text-slate-500 uppercase tracking-wider mt-2">
                            Documents
                          </div>
                          
                          {/* Reports */}
                          {userPermissions?.view_reports && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/quarterly-reports"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Reports
                            </DropdownMenuItem>
                          )}
                          
                          {/* Document Templates */}
                          {(userPermissions?.send_documents || userRole === 'admin') && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/document-templates"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Templates
                            </DropdownMenuItem>
                          )}
                        </>
                      )}

                      {/* --- OFFICER TOOLS GROUP (Feb 2026 — QR Codes + Transcriptions) --- */}
                      {(userPermissions?.manage_qr_codes || userPermissions?.manage_linktree || userPermissions?.manage_email_groups || hasSharedEmailGroups || userRole === 'admin' || userPermissions?.view_transcriptions) && (
                        <>
                          <div className="px-2 py-1 text-[10px] font-medium text-slate-500 uppercase tracking-wider mt-2">
                            Officer Tools
                          </div>

                          {userPermissions?.manage_qr_codes && (
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); navigate("/qr-codes"); }}
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-qr-codes"
                            >
                              <QrCode className="w-4 h-4 mr-2" />
                              QR Codes
                            </DropdownMenuItem>
                          )}

                          {userPermissions?.manage_linktree && (
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); navigate("/linktree-admin"); }}
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-linktree"
                            >
                              <Link2 className="w-4 h-4 mr-2" />
                              Link Tree
                            </DropdownMenuItem>
                          )}

                          {(userPermissions?.manage_email_groups || hasSharedEmailGroups) && (
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); navigate("/email-groups"); }}
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-email-groups"
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Email Groups
                            </DropdownMenuItem>
                          )}

                          {(userRole === 'admin' || userPermissions?.view_transcriptions) && (
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); navigate("/transcriptions"); }}
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-transcriptions"
                            >
                              <Mic className="w-4 h-4 mr-2" />
                              Meeting Transcriptions
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                      
                      {/* --- FINANCE GROUP --- */}
                      {(userPermissions?.view_treasury || userPermissions?.manage_treasury || userPermissions?.treasury_admin || userPermissions?.manage_dues_reminders) && (
                        <>
                          <div className="px-2 py-1 text-[10px] font-medium text-slate-500 uppercase tracking-wider mt-2">
                            Finance
                          </div>
                          
                          {/* Treasury */}
                          {(userPermissions?.view_treasury || userPermissions?.manage_treasury || userPermissions?.treasury_admin) && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/treasury"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <DollarSign className="w-4 h-4 mr-2" />
                              Treasury
                            </DropdownMenuItem>
                          )}
                          
                          {/* Dues Reminders */}
                          {userPermissions?.manage_dues_reminders && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/dues-reminders"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Dues Reminders
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                      
                      {/* --- DISCORD GROUP --- */}
                      {(userPermissions?.manage_discord_bots || userPermissions?.manage_polls || userPermissions?.view_discord_analytics || userChapter === 'National') && (
                        <>
                          <div className="px-2 py-1 text-[10px] font-medium text-slate-500 uppercase tracking-wider mt-2">
                            Discord
                          </div>
                          
                          {/* Discord Bots */}
                          {userPermissions?.manage_discord_bots && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/discord-bots"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <Bot className="w-4 h-4 mr-2" />
                              Bots
                            </DropdownMenuItem>
                          )}
                          
                          {/* Discord ↔ Member/Prospect/Hangaround Linking */}
                          {userPermissions?.manage_discord_bots && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/discord-linking"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-discord-linking"
                            >
                              <Link2 className="w-4 h-4 mr-2" />
                              Member Linking
                            </DropdownMenuItem>
                          )}
                          
                          {/* Discord Departures Log */}
                          {userPermissions?.manage_discord_bots && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/departures"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-departures"
                            >
                              <UserMinus className="w-4 h-4 mr-2" />
                              Departures
                            </DropdownMenuItem>
                          )}
                          
                          {/* Discord Analytics - Permission Panel controlled (view_discord_analytics) */}
                          {userPermissions?.view_discord_analytics && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/discord-analytics"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-discord-analytics"
                            >
                              <Headphones className="w-4 h-4 mr-2" />
                              Analytics
                            </DropdownMenuItem>
                          )}
                          
                          {/* Scriptly Meeting Notes - Nationals only */}
                          {userChapter === 'National' && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/scriptly"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-scriptly"
                            >
                              <Mic className="w-4 h-4 mr-2" />
                              Scriptly Notes
                            </DropdownMenuItem>
                          )}
                          
                          {/* Voting Polls entry moved to the main COMMUNICATION menu (visible to ALL members) */}
                        </>
                      )}
                      
                      {/* --- SYSTEM GROUP --- */}
                      {(userPermissions?.manage_system_users || userPermissions?.manage_nfc || (userChapter === 'National' && titleLists.national_admin_titles.includes(userTitle)) || localStorage.getItem("username") === "Lonestar") && (
                        <>
                          <div className="px-2 py-1 text-[10px] font-medium text-slate-500 uppercase tracking-wider mt-2">
                            System
                          </div>
                          
                          {/* System Users */}
                          {userPermissions?.manage_system_users && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/users"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              Users
                            </DropdownMenuItem>
                          )}
                          
                          {/* Title Editor - National admin titles */}
                          {userChapter === 'National' && titleLists.national_admin_titles.includes(userTitle) && (
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); navigate("/title-editor"); }}
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-title-editor"
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Title Editor
                            </DropdownMenuItem>
                          )}

                          {/* Chapter Editor - National CTO only */}
                          {userChapter === 'National' && userTitle === 'CTO' && (
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); navigate("/chapter-editor"); }}
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-chapter-editor"
                            >
                              <Layers className="w-4 h-4 mr-2" />
                              Chapter Editor
                            </DropdownMenuItem>
                          )}

                          {/* Permissions Panel - National Prez, VP, SEC, T only */}
                          {userChapter === 'National' && titleLists.national_admin_titles.includes(userTitle) && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/permissions"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <Shield className="w-4 h-4 mr-2" />
                              Permissions
                            </DropdownMenuItem>
                          )}
                          
                          {/* Change Log */}
                          {userPermissions?.manage_system_users && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/update-log"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <Clock className="w-4 h-4 mr-2" />
                              Change Log
                            </DropdownMenuItem>
                          )}
                          
                          {/* AI Knowledge Manager - Nationals only */}
                          {userChapter === 'National' && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/ai-knowledge"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <Database className="w-4 h-4 mr-2" />
                              AI Knowledge
                            </DropdownMenuItem>
                          )}
                          
                          {/* NFC Card Manager */}
                          {userPermissions?.manage_nfc && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/nfc"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <CreditCard className="w-4 h-4 mr-2" />
                              NFC Cards
                            </DropdownMenuItem>
                          )}
                          
                          {/* Msg Monitor - Lonestar/Repo/Gear Jammer only (legacy hardcoded) */}
                          {["Lonestar", "Repoman", "Repo", "Gear Jammer", "GearJammer"].includes(localStorage.getItem("username")) && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/message-monitor"); }} 
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Msg Monitor
                            </DropdownMenuItem>
                          )}

                          {/* Control Tool - National + manage_control_tool permission (granted via Permission Panel) */}
                          {userChapter === 'National' && userPermissions?.manage_control_tool && (
                            <DropdownMenuItem 
                              onSelect={(e) => { e.preventDefault(); navigate("/disconnect-tool"); }} 
                              className="text-yellow-400 focus:bg-yellow-900/30 focus:text-yellow-300 cursor-pointer pl-4"
                              data-testid="menu-control-tool"
                            >
                              <Headphones className="w-4 h-4 mr-2" />
                              Control Tool
                            </DropdownMenuItem>
                          )}

                          {/* Refactor Health - National Prez/VP/SEC only (matches backend gate) */}
                          {userChapter === 'National' && titleLists.leadership_core_titles.includes(userTitle) && (
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); navigate("/refactor-health"); }}
                              className="text-red-400 focus:bg-red-900/30 focus:text-red-300 cursor-pointer pl-4"
                              data-testid="menu-refactor-health"
                            >
                              <GitBranch className="w-4 h-4 mr-2" />
                              Refactor Health
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                    </>
                  )}
                  
                  {/* === SETTINGS === */}
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem 
                    onSelect={(e) => { e.preventDefault(); setPasswordDialogOpen(true); }} 
                    className="text-slate-200 focus:bg-slate-700 focus:text-white cursor-pointer"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <h1 className="text-xl sm:text-2xl font-bold text-white">Brothers of the Highway</h1>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-xs sm:text-sm text-slate-300">
                {localStorage.getItem("username")} ({userRole}{userChapter ? ` - ${userChapter}` : ''})
              </span>
              <ForceRefreshButton userRole={userRole} userChapter={userChapter} />
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                data-testid="logout-button"
                className="flex items-center gap-2 text-xs sm:text-sm bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Dues catch-up receipt popup */}
      {myDues?.linked && <DuesReceiptPopup />}

      {/* My Dues Section */}
      {myDues?.linked && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="bg-slate-800 rounded-xl shadow-sm border border-slate-700 p-4 sm:p-6">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setMyDuesExpanded(!myDuesExpanded)}
            >
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-green-400" />
                <h2 className="text-lg font-semibold text-white">My Dues</h2>
                <Badge className={myDues?.current_month_status === 'paid' ? 'bg-green-600' : 'bg-red-600'}>
                  {myDues?.current_month || 'N/A'}: {myDues?.current_month_status === 'paid' ? 'Paid' : 'Unpaid'}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400">
                {myDuesExpanded ? '▲' : '▼'}
              </Button>
            </div>
            
            {myDuesExpanded && (
              <div className="mt-4 space-y-4">
                <DuesReceiptHistory />
                {/* Helper function to get payment info for a month */}
                {(() => {
                  // Build a map of months to payment info from one_time_payments and square_payments
                  const getPaymentInfoForMonth = (year, monthIdx) => {
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    
                    // Check payment_info_by_year first
                    const paymentNote = myDues.payment_info_by_year?.[String(year)]?.[monthNames[monthIdx]];
                    if (paymentNote) return paymentNote;
                    
                    // Check one_time_payments
                    const oneTimePayments = myDues.one_time_payments || [];
                    for (const payment of oneTimePayments) {
                      if (payment.payment_date) {
                        const paymentDate = new Date(payment.payment_date);
                        const paymentYear = paymentDate.getFullYear();
                        const paymentMonth = paymentDate.getMonth();
                        const monthsCovered = payment.months_covered || 1;
                        
                        // Check if this payment covers the requested month
                        for (let i = 0; i < monthsCovered; i++) {
                          const coveredMonth = (paymentMonth + i) % 12;
                          const coveredYear = paymentYear + Math.floor((paymentMonth + i) / 12);
                          if (coveredYear === year && coveredMonth === monthIdx) {
                            return `$${payment.amount?.toFixed(2) || '0.00'} paid on ${paymentDate.toLocaleDateString()}`;
                          }
                        }
                      }
                    }
                    
                    // Check square_payments
                    const squarePayments = myDues.square_payments || [];
                    for (const payment of squarePayments) {
                      if (payment.paid_at && payment.status === 'PAID') {
                        const paymentDate = new Date(payment.paid_at);
                        const paymentYear = paymentDate.getFullYear();
                        const paymentMonth = paymentDate.getMonth();
                        if (paymentYear === year && paymentMonth === monthIdx) {
                          return `$${payment.amount?.toFixed(2) || '30.00'} paid on ${paymentDate.toLocaleDateString()}`;
                        }
                      }
                    }
                    
                    return null;
                  };
                  
                  return null;
                })()}
                
                {/* Current Year Status */}
                <div>
                  <h3 className="text-sm font-medium text-slate-400 mb-2">2025 Dues Status</h3>
                  <div className="flex flex-wrap gap-2">
                    <TooltipProvider>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => {
                        const yearDues = myDues.dues_by_year?.['2025'] || [];
                        const monthData = yearDues[idx];
                        const isPaid = monthData === true || (typeof monthData === 'object' && monthData?.status === 'paid');
                        
                        // Get payment info from various sources
                        let paymentInfo = myDues.payment_info_by_year?.['2025']?.[month];
                        
                        // Check one_time_payments
                        if (!paymentInfo && isPaid) {
                          const oneTimePayments = myDues.one_time_payments || [];
                          for (const payment of oneTimePayments) {
                            if (payment.payment_date) {
                              const paymentDate = new Date(payment.payment_date);
                              const paymentYear = paymentDate.getFullYear();
                              const paymentMonth = paymentDate.getMonth();
                              const monthsCovered = payment.months_covered || 1;
                              
                              for (let i = 0; i < monthsCovered; i++) {
                                const coveredMonth = (paymentMonth + i) % 12;
                                const coveredYear = paymentYear + Math.floor((paymentMonth + i) / 12);
                                if (coveredYear === 2025 && coveredMonth === idx) {
                                  paymentInfo = `$${payment.amount?.toFixed(2) || '0.00'} paid on ${paymentDate.toLocaleDateString()}`;
                                  break;
                                }
                              }
                              if (paymentInfo) break;
                            }
                          }
                        }
                        
                        // Check square_payments
                        if (!paymentInfo && isPaid) {
                          const squarePayments = myDues.square_payments || [];
                          for (const payment of squarePayments) {
                            if (payment.paid_at && payment.status === 'PAID') {
                              const paymentDate = new Date(payment.paid_at);
                              if (paymentDate.getFullYear() === 2025 && paymentDate.getMonth() === idx) {
                                paymentInfo = `$${payment.amount?.toFixed(2) || '30.00'} paid on ${paymentDate.toLocaleDateString()}`;
                                break;
                              }
                            }
                          }
                        }
                        
                        return (
                          <Tooltip key={month}>
                            <TooltipTrigger asChild>
                              <div 
                                className={`px-3 py-1 rounded text-xs font-medium cursor-pointer transition-colors ${
                                  isPaid 
                                    ? 'bg-green-600/30 text-green-400 border border-green-600 hover:bg-green-600/50' 
                                    : 'bg-slate-700 text-slate-400 border border-slate-600 hover:bg-slate-600'
                                }`}
                              >
                                {month}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="bg-slate-700 border-slate-600 text-white max-w-xs">
                              <p className="font-medium">{month} 2025</p>
                              {isPaid ? (
                                <p className="text-green-400 text-xs">
                                  {paymentInfo || 'Paid'}
                                </p>
                              ) : (
                                <p className="text-slate-400 text-xs">Unpaid</p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </TooltipProvider>
                  </div>
                </div>

                {/* 2026 Status */}
                {myDues.dues_by_year?.['2026'] && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-400 mb-2">2026 Dues Status</h3>
                    <div className="flex flex-wrap gap-2">
                      <TooltipProvider>
                        {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => {
                          const yearDues = myDues.dues_by_year?.['2026'] || [];
                          const monthData = yearDues[idx];
                          const isPaid = monthData === true || (typeof monthData === 'object' && monthData?.status === 'paid');
                          
                          // Get payment info from various sources
                          let paymentInfo = myDues.payment_info_by_year?.['2026']?.[month];
                          
                          // Check one_time_payments
                          if (!paymentInfo && isPaid) {
                            const oneTimePayments = myDues.one_time_payments || [];
                            for (const payment of oneTimePayments) {
                              if (payment.payment_date) {
                                const paymentDate = new Date(payment.payment_date);
                                const paymentYear = paymentDate.getFullYear();
                                const paymentMonth = paymentDate.getMonth();
                                const monthsCovered = payment.months_covered || 1;
                                
                                for (let i = 0; i < monthsCovered; i++) {
                                  const coveredMonth = (paymentMonth + i) % 12;
                                  const coveredYear = paymentYear + Math.floor((paymentMonth + i) / 12);
                                  if (coveredYear === 2026 && coveredMonth === idx) {
                                    paymentInfo = `$${payment.amount?.toFixed(2) || '0.00'} paid on ${paymentDate.toLocaleDateString()}`;
                                    break;
                                  }
                                }
                                if (paymentInfo) break;
                              }
                            }
                          }
                          
                          // Check square_payments
                          if (!paymentInfo && isPaid) {
                            const squarePayments = myDues.square_payments || [];
                            for (const payment of squarePayments) {
                              if (payment.paid_at && payment.status === 'PAID') {
                                const paymentDate = new Date(payment.paid_at);
                                if (paymentDate.getFullYear() === 2026 && paymentDate.getMonth() === idx) {
                                  paymentInfo = `$${payment.amount?.toFixed(2) || '30.00'} paid on ${paymentDate.toLocaleDateString()}`;
                                  break;
                                }
                              }
                            }
                          }
                          
                          return (
                            <Tooltip key={month}>
                              <TooltipTrigger asChild>
                                <div 
                                  className={`px-3 py-1 rounded text-xs font-medium cursor-pointer transition-colors ${
                                    isPaid 
                                      ? 'bg-green-600/30 text-green-400 border border-green-600 hover:bg-green-600/50' 
                                      : 'bg-slate-700 text-slate-400 border border-slate-600 hover:bg-slate-600'
                                  }`}
                                >
                                  {month}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-slate-700 border-slate-600 text-white max-w-xs">
                                <p className="font-medium">{month} 2026</p>
                                {isPaid ? (
                                  <p className="text-green-400 text-xs">
                                    {paymentInfo || 'Paid'}
                                  </p>
                                ) : (
                                  <p className="text-slate-400 text-xs">Unpaid</p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </TooltipProvider>
                    </div>
                  </div>
                )}

                {/* Subscription Info */}
                {myDues.has_subscription && myDues.subscription_info && (
                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <h3 className="text-sm font-medium text-slate-300 mb-1">Active Subscription</h3>
                    <p className="text-xs text-slate-400">
                      Square Customer: {myDues.subscription_info.customer_name || 'Linked'}
                    </p>
                  </div>
                )}

                {/* Recent Payments */}
                {(myDues.square_payments?.length > 0 || myDues.one_time_payments?.length > 0) && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-400 mb-2">Recent Payments</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {/* Combine and deduplicate payments */}
                      {(() => {
                        const seen = new Set();
                        const allPayments = [];
                        
                        // Add subscription payments first (these are more authoritative)
                        myDues.square_payments?.forEach(payment => {
                          const key = payment.payment_id || `sub-${payment.invoice_id}`;
                          const dateKey = payment.paid_at ? new Date(payment.paid_at).toDateString() : null;
                          
                          if (!seen.has(key)) {
                            seen.add(key);
                            // Also track by date+amount to catch duplicates from synced_payment_links
                            if (dateKey && payment.amount) {
                              seen.add(`${dateKey}-${payment.amount}`);
                            }
                            allPayments.push({
                              ...payment,
                              type: 'subscription',
                              displayDate: payment.paid_at,
                              sortDate: payment.paid_at || payment.invoice_date
                            });
                          }
                        });
                        
                        // Add one-time payments, skipping duplicates
                        // Skip if same date+amount combination already exists (from subscription payments)
                        myDues.one_time_payments?.forEach(payment => {
                          const key = payment.payment_id || payment.square_payment_id || `otp-${payment.order_id || Math.random()}`;
                          const dateKey = payment.payment_date ? new Date(payment.payment_date).toDateString() : null;
                          const dateAmountKey = dateKey && payment.amount ? `${dateKey}-${payment.amount}` : null;
                          
                          // Skip if we already have this payment by ID or by date+amount
                          if (seen.has(key) || (dateAmountKey && seen.has(dateAmountKey))) {
                            return;
                          }
                          
                          seen.add(key);
                          if (dateAmountKey) seen.add(dateAmountKey);
                          
                          allPayments.push({
                            ...payment,
                            type: 'one_time',
                            displayDate: payment.payment_date,
                            sortDate: payment.payment_date
                          });
                        });
                        
                        // Sort by date descending and take first 5
                        return allPayments
                          .sort((a, b) => new Date(b.sortDate || 0) - new Date(a.sortDate || 0))
                          .slice(0, 5)
                          .map((payment, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-slate-700/50 rounded p-2 text-sm">
                              <div>
                                <span className="text-green-400 font-medium">${payment.amount?.toFixed(2)}</span>
                                <span className="text-slate-400 ml-2">
                                  {payment.displayDate ? new Date(payment.displayDate).toLocaleDateString() : 'Pending'}
                                </span>
                                {payment.type === 'one_time' && payment.months_covered && (
                                  <span className="text-slate-500 ml-2 text-xs">
                                    ({payment.months_covered} month{payment.months_covered > 1 ? 's' : ''})
                                  </span>
                                )}
                              </div>
                              <Badge className={
                                payment.status === 'PAID' || payment.status === 'PAID_OTP' || payment.type === 'one_time'
                                  ? (payment.status === 'PAID_OTP' ? 'bg-cyan-600' : 'bg-green-600')
                                  : 'bg-yellow-600'
                              }>
                                {payment.status === 'PAID_OTP' ? 'Paid (one-time)' : (payment.status || 'Paid')}
                              </Badge>
                            </div>
                          ));
                      })()}
                    </div>
                  </div>
                )}

                {/* No payments message */}
                {!myDues.square_payments?.length && !myDues.one_time_payments?.length && (
                  <p className="text-sm text-slate-400">No payment history found. Contact your chapter SEC for assistance.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* My Quizzes Section — pending quizzes the member is eligible for */}
      {myQuizzes.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4" data-testid="my-quizzes">
          <div className="bg-slate-800 rounded-xl shadow-sm border border-slate-700 p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-3">
              <ClipboardList className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-semibold text-white">Pending Quizzes</h2>
              <Badge className="bg-blue-600 hover:bg-blue-600">{myQuizzes.length}</Badge>
            </div>
            <div className="space-y-2">
              {myQuizzes.map((q) => (
                <div
                  key={q.id}
                  className="flex items-center justify-between bg-slate-900/60 rounded-lg p-3 border border-slate-700"
                  data-testid={`my-quiz-${q.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-semibold truncate">{q.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {q.question_count} questions · pass {q.passing_pct}%
                      {q.deadline && ` · due ${q.deadline}`}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/quiz/${q.id}`)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid={`take-quiz-${q.id}`}
                  >
                    Take quiz
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* My Financial Assistance Section — visible to any member with active ledger entries */}
      {myAssistance?.linked && myAssistance.entries?.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4" data-testid="my-financial-assistance">
          <div className="bg-slate-800 rounded-xl shadow-sm border border-slate-700 p-4 sm:p-6">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setMyAssistanceExpanded(!myAssistanceExpanded)}
            >
              <div className="flex items-center gap-3">
                <HandCoins className="w-5 h-5 text-emerald-400" />
                <h2 className="text-lg font-semibold text-white">My Financial Assistance</h2>
                {myAssistance.totals?.balance > 0 ? (
                  <Badge className="bg-amber-600 hover:bg-amber-600">
                    Balance ${Number(myAssistance.totals.balance).toFixed(2)}
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600">Paid In Full</Badge>
                )}
                <span className="text-xs text-slate-400 hidden sm:inline">
                  {myAssistance.entries.length} entr{myAssistance.entries.length === 1 ? 'y' : 'ies'}
                </span>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400" data-testid="my-fa-toggle">
                {myAssistanceExpanded ? '▲' : '▼'}
              </Button>
            </div>

            {/* compact summary line (visible always) */}
            <div className="mt-3 grid grid-cols-3 gap-3 text-center">
              <div className="bg-slate-900/60 rounded-lg p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Received</div>
                <div className="text-lg font-bold text-white">
                  ${Number(myAssistance.totals?.amount_received || 0).toFixed(2)}
                </div>
              </div>
              <div className="bg-slate-900/60 rounded-lg p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Repaid</div>
                <div className="text-lg font-bold text-emerald-400">
                  ${Number(myAssistance.totals?.paid_to_date || 0).toFixed(2)}
                </div>
              </div>
              <div className="bg-slate-900/60 rounded-lg p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Balance</div>
                <div
                  className={
                    'text-lg font-bold ' +
                    (myAssistance.totals?.balance > 0 ? 'text-amber-400' : 'text-slate-300')
                  }
                >
                  ${Number(myAssistance.totals?.balance || 0).toFixed(2)}
                </div>
              </div>
            </div>

            {myAssistanceExpanded && (
              <div className="mt-4 space-y-3" data-testid="my-fa-details">
                {myAssistance.entries.map((e) => {
                  const status = e.status || 'open';
                  const sty =
                    status === 'paid_in_full'
                      ? 'bg-emerald-600'
                      : status === 'partial'
                      ? 'bg-amber-500'
                      : 'bg-red-600';
                  const label =
                    status === 'paid_in_full'
                      ? 'Paid In Full'
                      : status === 'partial'
                      ? 'Partial'
                      : 'Open';
                  return (
                    <div
                      key={e.id}
                      className="bg-slate-900/60 rounded-lg p-3 border border-slate-700"
                      data-testid={`my-fa-entry-${e.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-sm text-white font-semibold">
                            ${Number(e.amount_received).toFixed(2)} received on {e.date_received}
                          </div>
                          {e.reason && (
                            <div className="text-xs text-slate-400 mt-0.5">{e.reason}</div>
                          )}
                        </div>
                        <Badge className={`${sty} hover:${sty} text-white text-xs`}>{label}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-slate-400">Repaid: </span>
                          <span className="text-emerald-400 font-semibold">
                            ${Number(e.paid_to_date).toFixed(2)}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400">Balance: </span>
                          <span
                            className={
                              e.balance > 0
                                ? 'text-amber-400 font-semibold'
                                : 'text-slate-300 font-semibold'
                            }
                          >
                            ${Number(e.balance).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {e.payments && e.payments.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-700">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                            Payments ({e.payments.length})
                          </div>
                          <div className="space-y-1">
                            {e.payments
                              .slice()
                              .reverse()
                              .map((p) => (
                                <div
                                  key={p.id}
                                  className="flex justify-between text-xs text-slate-300"
                                >
                                  <span>
                                    {p.date} · <span className="capitalize">{p.method}</span>
                                  </span>
                                  <span className="text-emerald-400 font-semibold">
                                    ${Number(p.amount).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <p className="text-[11px] text-slate-500 italic">
                  Questions about your assistance balance? Contact a National officer.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members Table Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="bg-slate-800 rounded-xl shadow-sm border border-slate-700 p-4 sm:p-6">
          <div className="mb-4 sm:mb-6">
            <div className="relative max-w-2xl">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none z-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                placeholder="Search by name or handle..."
                data-testid="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 py-4 sm:py-6 text-sm sm:text-base border-2 border-slate-600 focus:border-slate-400 rounded-lg bg-slate-700 text-white placeholder:text-slate-400"
              />
            </div>
            
            {/* Chapter Tabs */}
            <div className="flex flex-wrap gap-2 mt-4">
              {["All", ...activeChapters].map((chapter) => (
                <button
                  key={chapter}
                  onClick={() => setSelectedChapter(chapter)}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedChapter === chapter
                      ? "bg-blue-600 text-white shadow-lg"
                      : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                  }`}
                >
                  {chapter}
                  <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                    selectedChapter === chapter
                      ? "bg-blue-500 text-white"
                      : "bg-slate-600 text-slate-400"
                  }`}>
                    {chapterCounts[chapter]}
                  </span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Total Experience Badge - Visible to all members */}
          {totalExperience && totalExperience.total_years > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 mb-4 bg-blue-900/50 rounded-lg border border-blue-700/50 w-fit">
              <span className="text-lg">🚛</span>
              <span className="text-sm font-medium text-blue-300">
                {totalExperience.total_years_formatted}+ <span className="text-blue-400/80">Years Experience</span>
              </span>
            </div>
          )}
          
          {hasPermission('admin_actions') && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
              {/* Member Count Badge */}
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-700 rounded-lg border border-slate-600">
                <Users className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-white">
                  {sortedFilteredMembers.length} <span className="text-slate-400">{selectedChapter === "All" ? "Members" : selectedChapter + " Members"}</span>
                </span>
                {(searchTerm || selectedChapter !== "All") && sortedFilteredMembers.length !== members.length && (
                  <span className="text-xs text-slate-400">
                    (of {members.length} total)
                  </span>
                )}
              </div>
              
              {/* View Export Button - Permission controlled */}
              {(userRole === 'admin' || userPermissions?.view_export) && (
                <Button
                  onClick={handleViewCSV}
                  size="sm"
                  className="flex items-center justify-center gap-2 w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Export
                </Button>
              )}

              {/* Bulk Send Questionnaire — National admin only */}
              {isNationalAdmin && (
                <Button
                  onClick={() => { setBulkQSelected([]); setBulkQSearch(""); setBulkQDialogOpen(true); }}
                  size="sm"
                  className="flex items-center justify-center gap-2 w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="bulk-questionnaire-button"
                >
                  <ClipboardList className="w-4 h-4" />
                  Send Questionnaire
                </Button>
              )}
            </div>
          )}

          {/* Bulk Send Profile Questionnaire Dialog (National admin only) */}
          <Dialog open={bulkQDialogOpen} onOpenChange={setBulkQDialogOpen}>
            <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85vh] flex flex-col bg-slate-800 border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-lg text-white">Send Profile Questionnaire</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-400">
                Selected members receive a Discord DM with a 72-hour link to review &amp; update
                their profile. Members who completed a questionnaire within the last year, or
                have no Discord linked, are skipped automatically.
              </p>
              <Input
                placeholder="Search by handle, name, or chapter…"
                value={bulkQSearch}
                onChange={(e) => setBulkQSearch(e.target.value)}
                className="text-white"
                data-testid="bulk-questionnaire-search"
              />
              {(() => {
                const q = bulkQSearch.toLowerCase();
                const visible = members.filter((m) =>
                  !q ||
                  (m.handle || "").toLowerCase().includes(q) ||
                  (m.name || "").toLowerCase().includes(q) ||
                  (m.chapter || "").toLowerCase().includes(q)
                );
                const visibleIds = visible.map((m) => m.id);
                const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => bulkQSelected.includes(id));
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() =>
                          setBulkQSelected(allVisibleSelected
                            ? bulkQSelected.filter((id) => !visibleIds.includes(id))
                            : [...new Set([...bulkQSelected, ...visibleIds])])
                        }
                        className="text-sm text-blue-400 hover:text-blue-300"
                        data-testid="bulk-questionnaire-select-all"
                      >
                        {allVisibleSelected ? "Deselect all shown" : "Select all shown"}
                      </button>
                      <span className="text-xs text-slate-400">{bulkQSelected.length} selected</span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-1 border border-slate-700 rounded-lg p-2 min-h-[200px] max-h-[40vh]">
                      {visible.map((m) => (
                        <label
                          key={m.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-700/50 cursor-pointer"
                          data-testid={`bulk-questionnaire-member-${m.id}`}
                        >
                          <Checkbox
                            checked={bulkQSelected.includes(m.id)}
                            onCheckedChange={(checked) =>
                              setBulkQSelected(checked
                                ? [...bulkQSelected, m.id]
                                : bulkQSelected.filter((id) => id !== m.id))
                            }
                            className="border-slate-400 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                          />
                          <span className="text-sm text-white">{m.handle}</span>
                          <span className="text-xs text-slate-400">{m.chapter} · {m.title}</span>
                          {questionnaireStatus[m.id] === "completed" && (
                            <ClipboardCheck className="w-3.5 h-3.5 text-green-400 ml-auto" />
                          )}
                          {questionnaireStatus[m.id] === "pending" && (
                            <Clock className="w-3.5 h-3.5 text-amber-400 ml-auto" />
                          )}
                        </label>
                      ))}
                      {visible.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-6">No members match your search</p>
                      )}
                    </div>
                  </>
                );
              })()}
              <div className="flex gap-3 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setBulkQDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkSendQuestionnaires}
                  disabled={bulkQSending || bulkQSelected.length === 0}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="bulk-questionnaire-send-btn"
                >
                  {bulkQSending ? "Sending…" : `Send to ${bulkQSelected.length} member(s)`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Member Add/Edit Dialog — must render for ALL users who can edit at least their own chapter,
              even without admin_actions permission. The "Add Member" trigger inside is gated by admin_actions. */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                {hasPermission('admin_actions') && (
                  <DialogTrigger asChild>
                    <Button
                      data-testid="add-member-button"
                      size="sm"
                      className="flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 text-white w-full sm:w-auto"
                    >
                      <Plus className="w-4 h-4" />
                      Add Member
                    </Button>
                  </DialogTrigger>
                )}
                  <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700">
                    <DialogHeader>
                      <DialogTitle className="text-lg sm:text-xl text-white">
                        {editingMember ? "Edit Member" : "Add New Member"}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white">Chapter</Label>
                          <Select
                            value={formData.chapter}
                            onValueChange={(value) =>
                              setFormData({ ...formData, chapter: value })
                            }
                            required
                          >
                            <SelectTrigger data-testid="chapter-select" className="text-white">
                              <SelectValue placeholder="Select chapter" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 text-white border-slate-700">
                              {activeChapters.map((ch) => (
                                <SelectItem key={ch} value={ch} className="text-white hover:bg-slate-700">
                                  {ch}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white">Title</Label>
                          <Select
                            value={formData.title}
                            onValueChange={(value) =>
                              setFormData({ ...formData, title: value })
                            }
                            required
                          >
                            <SelectTrigger data-testid="title-select" className="text-white">
                              <SelectValue placeholder="Select title" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 text-white border-slate-700">
                              {(titleLists.all_titles || TITLES)
                                .filter((t) => {
                                  const m = titleLists.chapter_map?.[t];
                                  return !m || m.length === 0 || !formData.chapter || m.includes(formData.chapter) || t === formData.title;
                                })
                                .map((t) => (
                                <SelectItem key={t} value={t} className="text-white hover:bg-slate-700">
                                  {t}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label className="text-white">Member Handle</Label>
                        <Input
                          data-testid="handle-input"
                          value={formData.handle}
                          onChange={(e) =>
                            setFormData({ ...formData, handle: e.target.value })
                          }
                          required
                          className="text-white"
                        />
                      </div>

                      <div>
                        <Label className="text-white">Name</Label>
                        <Input
                          data-testid="name-input"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          required
                          className="text-white"
                        />
                        <div className="flex items-center space-x-2 mt-2">
                          <Checkbox
                            id="name_private"
                            checked={formData.name_private}
                            onCheckedChange={(checked) =>
                              setFormData({ ...formData, name_private: checked })
                            }
                            className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          />
                          <label htmlFor="name_private" className="text-sm font-medium cursor-pointer text-slate-300">
                            Make name private (visible only to National members & chapter officers)
                          </label>
                        </div>
                      </div>

                      <div>
                        <Label className="text-white">BOH Email</Label>
                        <Input
                          data-testid="email-input"
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          className="text-white"
                          placeholder="Club email address (optional)"
                        />
                        <div className="flex items-center space-x-2 mt-2">
                          <Checkbox
                            id="email_private"
                            checked={formData.email_private}
                            onCheckedChange={(checked) =>
                              setFormData({ ...formData, email_private: checked })
                            }
                            className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          />
                          <label htmlFor="email_private" className="text-sm font-medium cursor-pointer text-slate-300">
                            Make BOH email private
                          </label>
                        </div>
                      </div>

                      <div>
                        <Label className="text-white">Personal Email</Label>
                        <Input
                          data-testid="personal-email-input"
                          type="email"
                          value={formData.personal_email}
                          onChange={(e) =>
                            setFormData({ ...formData, personal_email: e.target.value })
                          }
                          className="text-white"
                          placeholder="Personal email address (optional)"
                        />
                        <div className="flex items-center space-x-2 mt-2">
                          <Checkbox
                            id="personal_email_private"
                            checked={formData.personal_email_private}
                            onCheckedChange={(checked) =>
                              setFormData({ ...formData, personal_email_private: checked })
                            }
                            className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          />
                          <label htmlFor="personal_email_private" className="text-sm font-medium cursor-pointer text-slate-300">
                            Make personal email private
                          </label>
                        </div>
                      </div>

                      <div>
                        <Label className="text-white">Phone</Label>
                        <Input
                          data-testid="phone-input"
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                          }
                          required
                          className="text-white"
                        />
                        <div className="flex items-center space-x-2 mt-2">
                          <Checkbox
                            id="phone_private"
                            checked={formData.phone_private}
                            onCheckedChange={(checked) =>
                              setFormData({ ...formData, phone_private: checked })
                            }
                            className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          />
                          <label htmlFor="phone_private" className="text-sm font-medium cursor-pointer text-slate-300">
                            Make phone number private (hide from non-admin users)
                          </label>
                        </div>
                      </div>

                      <div>
                        <Label className="text-white">Address</Label>
                        <Input
                          data-testid="address-input"
                          value={formData.address}
                          onChange={(e) =>
                            setFormData({ ...formData, address: e.target.value })
                          }
                          required
                          className="text-white"
                        />
                        <div className="flex items-center space-x-2 mt-2">
                          <Checkbox
                            id="address_private"
                            checked={formData.address_private}
                            onCheckedChange={(checked) =>
                              setFormData({ ...formData, address_private: checked })
                            }
                            className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          />
                          <label htmlFor="address_private" className="text-sm font-medium cursor-pointer text-slate-300">
                            Make address private (hide from non-admin users)
                          </label>
                        </div>
                      </div>

                      <div>
                        <Label className="text-white">Date of Birth (optional)</Label>
                        <Input
                          type="date"
                          value={formData.dob}
                          onChange={(e) =>
                            setFormData({ ...formData, dob: e.target.value })
                          }
                          className="text-white"
                        />
                      </div>

                      <div>
                        <Label className="text-white">Anniversary Date (MM/YYYY)</Label>
                        <Input
                          type="text"
                          placeholder="MM/YYYY"
                          value={formData.join_date}
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^\d/]/g, '');
                            // Auto-add slash after 2 digits
                            if (value.length === 2 && !value.includes('/') && formData.join_date.length < value.length) {
                              value = value + '/';
                            }
                            // Limit to 7 characters (MM/YYYY)
                            if (value.length <= 7) {
                              setFormData({ ...formData, join_date: value });
                            }
                          }}
                          className="text-white"
                        />
                      </div>

                      <div>
                        <Label className="text-white">🚛 Trucking Experience Start (MM/YYYY)</Label>
                        <Input
                          type="text"
                          placeholder="MM/YYYY"
                          value={formData.experience_start}
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^\d/]/g, '');
                            // Auto-add slash after 2 digits
                            if (value.length === 2 && !value.includes('/') && formData.experience_start.length < value.length) {
                              value = value + '/';
                            }
                            // Limit to 7 characters (MM/YYYY)
                            if (value.length <= 7) {
                              setFormData({ ...formData, experience_start: value });
                            }
                          }}
                          className="text-white"
                        />
                      </div>

                      {/* Military Service Section */}
                      <div className="space-y-3 p-3 bg-slate-800 rounded-lg border border-slate-700">
                        <Label className="text-white font-semibold">🎖️ Military Service</Label>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="military_service"
                            checked={formData.military_service || false}
                            onCheckedChange={(checked) =>
                              setFormData({ ...formData, military_service: checked, military_branch: checked ? formData.military_branch : "" })
                            }
                          />
                          <label htmlFor="military_service" className="text-sm text-slate-200 cursor-pointer">
                            Served in Military
                          </label>
                        </div>
                        {formData.military_service && (
                          <div className="ml-6">
                            <Label className="text-slate-300 text-sm">Branch of Service</Label>
                            <Select
                              value={formData.military_branch || ""}
                              onValueChange={(value) => setFormData({ ...formData, military_branch: value })}
                            >
                              <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                                <SelectValue placeholder="Select branch" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-600">
                                <SelectItem value="Army" className="text-white hover:bg-slate-700">Army</SelectItem>
                                <SelectItem value="Navy" className="text-white hover:bg-slate-700">Navy</SelectItem>
                                <SelectItem value="Air Force" className="text-white hover:bg-slate-700">Air Force</SelectItem>
                                <SelectItem value="Marines" className="text-white hover:bg-slate-700">Marines</SelectItem>
                                <SelectItem value="Coast Guard" className="text-white hover:bg-slate-700">Coast Guard</SelectItem>
                                <SelectItem value="Space Force" className="text-white hover:bg-slate-700">Space Force</SelectItem>
                                <SelectItem value="National Guard" className="text-white hover:bg-slate-700">National Guard</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>

                      {/* First Responder Section */}
                      <div className="space-y-3 p-3 bg-slate-800 rounded-lg border border-slate-700">
                        <Label className="text-white font-semibold">🚨 First Responder Service</Label>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_first_responder"
                            checked={formData.is_first_responder || false}
                            onCheckedChange={(checked) =>
                              setFormData({ ...formData, is_first_responder: checked })
                            }
                          />
                          <label htmlFor="is_first_responder" className="text-sm text-slate-200 cursor-pointer">
                            Served as First Responder (Police, Fire, or EMS)
                          </label>
                        </div>
                      </div>

                      {/* Emergency Contact Section */}
                      <div className="space-y-3 p-3 bg-slate-800 rounded-lg border border-slate-700">
                        <Label className="text-white font-semibold">📞 Emergency Contact</Label>
                        <div>
                          <Label className="text-slate-300 text-sm">Contact Name</Label>
                          <Input
                            data-testid="emergency-contact-name-input"
                            value={formData.emergency_contact_name || ""}
                            onChange={(e) =>
                              setFormData({ ...formData, emergency_contact_name: e.target.value })
                            }
                            className="text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-sm">Contact Phone</Label>
                          <Input
                            data-testid="emergency-contact-phone-input"
                            value={formData.emergency_contact_phone || ""}
                            onChange={(e) =>
                              setFormData({ ...formData, emergency_contact_phone: e.target.value })
                            }
                            className="text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-sm">Relationship</Label>
                          <Input
                            data-testid="emergency-contact-relationship-input"
                            value={formData.emergency_contact_relationship || ""}
                            onChange={(e) =>
                              setFormData({ ...formData, emergency_contact_relationship: e.target.value })
                            }
                            className="text-white"
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="emergency_contact_private"
                            checked={formData.emergency_contact_private || false}
                            onCheckedChange={(checked) =>
                              setFormData({ ...formData, emergency_contact_private: checked })
                            }
                            className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                            data-testid="emergency-contact-private-checkbox"
                          />
                          <label htmlFor="emergency_contact_private" className="text-sm font-medium cursor-pointer text-slate-300">
                            Make emergency contact private (visible only to chapter officers and National)
                          </label>
                        </div>
                        {editingMember?.directory_consent && (
                          <p className="text-xs text-slate-400">
                            §6.8.3 Directory consent: {{
                              email_only: "E-Mail Address Only",
                              phone_only: "Phone Number Only",
                              both: "Both E-Mail Address & Phone Number",
                              none: "No consent to release any information",
                            }[editingMember.directory_consent] || editingMember.directory_consent}
                          </p>
                        )}
                      </div>

                      {/* Non-Dues Paying (Exempt) Section */}
                      <div className="space-y-3 p-3 bg-amber-900/30 rounded-lg border border-amber-700">
                        <Label className="text-white font-semibold">💰 Dues Exemption</Label>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="non_dues_paying"
                            checked={formData.non_dues_paying || false}
                            onCheckedChange={(checked) =>
                              setFormData({ ...formData, non_dues_paying: checked })
                            }
                            data-testid="non-dues-paying-checkbox"
                          />
                          <label htmlFor="non_dues_paying" className="text-sm text-slate-200 cursor-pointer">
                            Non-Dues Paying Member (Honorary, Exempt)
                          </label>
                        </div>
                        {formData.non_dues_paying && (
                          <p className="text-xs text-amber-300 ml-6">
                            This member will not receive dues reminders and will not be included in dues tracking reports.
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <Label className="text-white">Dues Tracking</Label>
                          <div className="flex items-center gap-2">
                            <Label className="text-sm text-white">Year:</Label>
                            <Input
                              type="number"
                              value={selectedDuesYear}
                              onChange={(e) => handleDuesYearChange(parseInt(e.target.value))}
                              className="w-24 text-white"
                              data-testid="dues-year-input"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {monthNames.map((month, index) => {
                            const yearMonths = formData.dues[selectedDuesYear.toString()] || Array(12).fill(null).map(() => ({ status: "unpaid", note: "" }));
                            const monthDue = yearMonths[index] || { status: "unpaid", note: "" };
                            const status = typeof monthDue === 'object' ? monthDue.status : (monthDue ? 'paid' : 'unpaid');
                            const note = typeof monthDue === 'object' ? monthDue.note : '';
                            
                            return (
                            <div key={index} className="space-y-1">
                              <button
                                type="button"
                                onClick={() => handleDuesToggle(index)}
                                className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors ${
                                  status === 'paid'
                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                    : status === 'late'
                                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                                    : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                }`}
                                data-testid={`dues-month-${index}`}
                              >
                                {month}
                              </button>
                              {status === 'late' && (
                                <Input
                                  type="text"
                                  placeholder="Late reason..."
                                  value={note}
                                  onChange={(e) => handleDuesNoteChange(index, e.target.value)}
                                  className="text-xs bg-slate-900 border-slate-700 text-slate-100"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                            </div>
                          )})}
                        </div>
                        <div className="text-xs text-slate-400 space-y-1">
                          <p>Click to cycle: <span className="text-slate-200">Unpaid</span> → <span className="text-green-400">Paid</span> → <span className="text-yellow-400">Late</span></p>
                          <p>Add note when marked as Late</p>
                        </div>
                      </div>

                      {/* Collapsible Meeting Attendance Section */}
                      <div className="border border-slate-600 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setAttendanceExpanded(!attendanceExpanded)}
                          className="w-full flex items-center justify-between p-3 bg-slate-700/50 hover:bg-slate-700 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Label className="cursor-pointer">Meeting Attendance ({selectedYear})</Label>
                            {/* Summary counts when collapsed */}
                            {(() => {
                              const yearMeetings = formData.meeting_attendance[selectedYear] || [];
                              const present = yearMeetings.filter(m => m?.status === 1).length;
                              const excused = yearMeetings.filter(m => m?.status === 2).length;
                              const absent = yearMeetings.filter(m => m?.status === 0).length;
                              const total = yearMeetings.length;
                              return (
                                <div className="flex gap-2 text-xs">
                                  <span className="px-2 py-0.5 bg-slate-600 text-white rounded">{total} total</span>
                                  <span className="px-2 py-0.5 bg-green-600 text-white rounded">{present} P</span>
                                  <span className="px-2 py-0.5 bg-orange-500 text-white rounded">{excused} E</span>
                                  <span className="px-2 py-0.5 bg-red-600/80 text-white rounded">{absent} A</span>
                                </div>
                              );
                            })()}
                          </div>
                          <svg 
                            className={`w-5 h-5 text-slate-400 transition-transform ${attendanceExpanded ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {attendanceExpanded && (
                          <div className="p-3 space-y-3 bg-slate-800/50">
                            {/* Year Selector and Add Meeting Button */}
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-400">Year:</span>
                                <select
                                  value={selectedYear}
                                  onChange={(e) => setSelectedYear(e.target.value)}
                                  className="bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1"
                                >
                                  {[...new Set([...availableYears, new Date().getFullYear().toString()])].sort((a, b) => b - a).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                  ))}
                                </select>
                              </div>
                              {userRole === 'admin' && (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => setAddMeetingDialogOpen(true)}
                                  className="bg-blue-600 hover:bg-blue-700 text-xs h-7"
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Meeting
                                </Button>
                              )}
                            </div>
                            
                            {/* Legend */}
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-slate-400">Click to cycle status</span>
                              <div className="flex gap-2">
                                <span className="text-green-500">● Present</span>
                                <span className="text-orange-500">● Excused</span>
                                <span className="text-red-500">● Absent</span>
                              </div>
                            </div>
                            
                            {/* Flexible Meetings List */}
                            {(() => {
                              const yearMeetings = formData.meeting_attendance[selectedYear] || [];
                              const isCurrentYear = selectedYear === new Date().getFullYear().toString();
                              
                              if (yearMeetings.length === 0) {
                                return (
                                  <div className="text-center py-4 text-slate-500 text-sm">
                                    No meetings recorded for {selectedYear}
                                    {userRole === 'admin' && <p className="text-xs mt-1">Click &quot;Add Meeting&quot; to add one</p>}
                                  </div>
                                );
                              }
                              
                              return (
                                <div className="space-y-1 max-h-48 overflow-y-auto">
                                  {yearMeetings.map((meeting, idx) => (
                                    <div 
                                      key={idx} 
                                      className="flex items-center gap-2 p-2 bg-slate-900/50 rounded"
                                    >
                                      {/* Date */}
                                      <span className="text-xs text-slate-300 w-20 flex-shrink-0">
                                        {formatMeetingDate(meeting.date)}
                                      </span>
                                      
                                      {/* Status Button */}
                                      <button
                                        type="button"
                                        onClick={() => isCurrentYear && handleAttendanceToggle(idx)}
                                        disabled={!isCurrentYear}
                                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                          meeting.status === 1
                                            ? 'bg-green-600 text-white'
                                            : meeting.status === 2
                                            ? 'bg-orange-500 text-white'
                                            : 'bg-red-600/80 text-white'
                                        } ${!isCurrentYear ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                                      >
                                        {meeting.status === 1 ? 'Present' : meeting.status === 2 ? 'Excused' : 'Absent'}
                                      </button>
                                      
                                      {/* Note Input */}
                                      {isCurrentYear ? (
                                        <Input
                                          type="text"
                                          value={meeting.note || ''}
                                          onChange={(e) => handleAttendanceNote(idx, e.target.value)}
                                          placeholder="Note..."
                                          className="flex-1 text-xs h-7 bg-slate-800 border-slate-600 text-white"
                                        />
                                      ) : (
                                        <span className="flex-1 text-xs text-slate-400 truncate">
                                          {meeting.note || '—'}
                                        </span>
                                      )}
                                      
                                      {/* Delete Button */}
                                      {userRole === 'admin' && isCurrentYear && (
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteMeeting(idx)}
                                          className="text-red-400 hover:text-red-300 p-1"
                                          title="Remove meeting"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Add Meeting Dialog */}
                      <Dialog open={addMeetingDialogOpen} onOpenChange={setAddMeetingDialogOpen}>
                        <DialogContent className="bg-slate-800 border-slate-600 max-w-sm">
                          <DialogHeader>
                            <DialogTitle className="text-white">Add Meeting</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label className="text-slate-200">Date</Label>
                              <Input
                                type="date"
                                value={newMeetingDate}
                                onChange={(e) => setNewMeetingDate(e.target.value)}
                                className="mt-1 bg-slate-700 border-slate-600 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-200">Status</Label>
                              <Select value={newMeetingStatus.toString()} onValueChange={(v) => setNewMeetingStatus(parseInt(v))}>
                                <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-600">
                                  <SelectItem value="1" className="text-white hover:bg-slate-700">Present</SelectItem>
                                  <SelectItem value="2" className="text-white hover:bg-slate-700">Excused</SelectItem>
                                  <SelectItem value="0" className="text-white hover:bg-slate-700">Absent</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-slate-200">Note (optional)</Label>
                              <Input
                                value={newMeetingNote}
                                onChange={(e) => setNewMeetingNote(e.target.value)}
                                placeholder="e.g., reason for absence"
                                className="mt-1 bg-slate-700 border-slate-600 text-white"
                              />
                            </div>
                            <div className="flex gap-3 justify-end pt-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setAddMeetingDialogOpen(false)}
                                className="text-white border-slate-600"
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                onClick={handleAddMeeting}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                Add Meeting
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <div className="flex gap-3 justify-end pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          className="text-white"
                          onClick={() => {
                            setDialogOpen(false);
                            resetForm();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          data-testid="submit-member-button"
                          className="bg-slate-800 hover:bg-slate-900 text-white"
                        >
                          {editingMember ? "Update" : "Add"} Member
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

          {loading ? (
            <div className="text-center py-12 text-slate-600">Loading members...</div>
          ) : sortedFilteredMembers.length === 0 ? (
            <div className="text-center py-12 text-slate-600">
              {searchTerm ? "No members found" : "No members yet. Add your first member!"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white">Chapter</TableHead>
                    <TableHead className="text-white">Title</TableHead>
                    <TableHead className="text-white">Handle</TableHead>
                    <TableHead className="text-white">Name</TableHead>
                    {(hasPermission('email') || true) && <TableHead className="text-white">Email</TableHead>}
                    <TableHead className="text-white">Personal Email</TableHead>
                    {hasPermission('phone') && <TableHead className="text-white">Phone</TableHead>}
                    {hasPermission('address') && <TableHead className="text-white">Address</TableHead>}
                    {hasPermission('dues_tracking') && <TableHead className="text-white">Dues</TableHead>}
                    {hasPermission('meeting_attendance') && <TableHead className="text-white">Attendance</TableHead>}
                    {(hasPermission('admin_actions') || userRole === 'admin') && <TableHead className="text-right text-white">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedFilteredMembers.map((member) => (
                    <TableRow key={member.id} data-testid={`member-row-${member.id}`}>
                      <TableCell className="font-medium text-white">{member.chapter}</TableCell>
                      <TableCell className="text-white">{member.title}</TableCell>
                      <TableCell className="text-white">
                        <div className="flex items-center gap-1.5">
                          <span>{member.handle}</span>
                          {questionnaireStatus[member.id] === "completed" && (
                            <span title="Member Profile Questionnaire completed" data-testid={`questionnaire-badge-${member.id}`}>
                              <ClipboardCheck className="w-4 h-4 text-green-400" />
                            </span>
                          )}
                          {(questionnaireStatus[member.id] === "pending" || questionnaireStatus[member.id] === "expired") && (
                            <span className="flex items-center gap-1" data-testid={`questionnaire-badge-${member.id}`}>
                              <span title={questionnaireStatus[member.id] === "pending" ? "Member Profile Questionnaire pending" : "Questionnaire link expired"}>
                                <Clock className={`w-3.5 h-3.5 ${questionnaireStatus[member.id] === "pending" ? "text-amber-400" : "text-red-400"}`} />
                              </span>
                              <button
                                type="button"
                                onClick={() => handleResendQuestionnaire(member)}
                                title="Resend questionnaire link via Discord DM"
                                className="p-0.5 rounded hover:bg-slate-700 text-blue-400 hover:text-blue-300"
                                data-testid={`questionnaire-resend-${member.id}`}
                              >
                                <Send className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-white">
                        {member.name || (isPendingQuestionnaire(member) ? (
                          <span className="text-slate-500 text-sm italic">pending questionnaire</span>
                        ) : "")}
                      </TableCell>
                      {(hasPermission('email') || true) && (
                        <TableCell>
                          {canSeeEmail(member) && member.email ? (
                            <a
                              href={`mailto:${member.email}`}
                              className="flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline text-sm"
                              data-testid={`email-link-${member.id}`}
                            >
                              <Mail className="w-3 h-3" />
                              <span className="text-white">{member.email}</span>
                            </a>
                          ) : !member.email && isPendingQuestionnaire(member) ? (
                            <span className="text-slate-500 text-sm italic">pending questionnaire</span>
                          ) : (
                            <span className="text-slate-500 text-sm">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {canSeePersonalEmail(member) && member.personal_email ? (
                          <a
                            href={`mailto:${member.personal_email}`}
                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline text-sm"
                            data-testid={`personal-email-link-${member.id}`}
                          >
                            <Mail className="w-3 h-3" />
                            <span className="text-white">{member.personal_email}</span>
                          </a>
                        ) : member.personal_email && member.personal_email_private ? (
                          <span className="text-slate-500 text-sm italic">Private</span>
                        ) : !member.personal_email && isPendingQuestionnaire(member) ? (
                          <span className="text-slate-500 text-sm italic">pending questionnaire</span>
                        ) : (
                          <span className="text-slate-500 text-sm">—</span>
                        )}
                      </TableCell>
                      {hasPermission('phone') && (
                        <TableCell>
                          {member.phone ? (
                            <a
                              href={`tel:${member.phone}`}
                              className="flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline text-sm"
                              data-testid={`phone-link-${member.id}`}
                            >
                              <Phone className="w-3 h-3" />
                              <span className="text-white">{member.phone}</span>
                            </a>
                          ) : isPendingQuestionnaire(member) ? (
                            <span className="text-slate-500 text-sm italic">pending questionnaire</span>
                          ) : (
                            <span className="text-slate-500 text-sm">—</span>
                          )}
                        </TableCell>
                      )}
                      {hasPermission('address') && (
                        <TableCell>
                          {member.address ? (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                member.address
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline text-sm"
                              data-testid={`address-link-${member.id}`}
                            >
                              <MapPin className="w-3 h-3" />
                              <span className="text-white">{member.address}</span>
                            </a>
                          ) : isPendingQuestionnaire(member) ? (
                            <span className="text-slate-500 text-sm italic">pending questionnaire</span>
                          ) : (
                            <span className="text-slate-500 text-sm">—</span>
                          )}
                        </TableCell>
                      )}
                      {hasPermission('dues_tracking') && (
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {(() => {
                              const dues = member.dues || {};
                              
                              // Handle old format where dues has 'year' and 'months' keys
                              if (dues.year && dues.months && Array.isArray(dues.months)) {
                                const paidCount = dues.months.filter(m => m === true).length;
                                return (
                                  <>
                                    <span className="text-xs text-white">{dues.year}</span>
                                    <div className="flex items-center gap-2 text-xs">
                                      <span className="text-green-400 font-medium">
                                        Paid: {paidCount}
                                      </span>
                                    </div>
                                  </>
                                );
                              }
                              
                              // Handle new format where dues is object with years as keys
                              const years = Object.keys(dues).filter(k => k !== 'year' && k !== 'months').sort((a, b) => b - a);
                              const latestYear = years[0] || new Date().getFullYear();
                              const months = dues[latestYear] || [];
                              
                              if (!Array.isArray(months) || months.length === 0) {
                                return (
                                  <>
                                    <span className="text-xs text-white">{latestYear}</span>
                                    <span className="text-xs text-white">No data</span>
                                  </>
                                );
                              }
                              
                              // Handle both old boolean format and new object format
                              const paidCount = months.filter(m => 
                                m && (typeof m === 'object' ? m.status === 'paid' : m === true)
                              ).length;
                              const lateCount = months.filter(m => 
                                m && typeof m === 'object' && m.status === 'late'
                              ).length;
                              
                              return (
                                <>
                                  <span className="text-xs text-white">{latestYear}</span>
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="text-green-400 font-medium">
                                      Paid: {paidCount}
                                    </span>
                                    {lateCount > 0 && (
                                      <span className="text-yellow-400 font-medium">
                                        Late: {lateCount}
                                      </span>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </TableCell>
                      )}
                      {hasPermission('meeting_attendance') && (
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            {(() => {
                              const currentYear = new Date().getFullYear().toString();
                              const attendance = member.meeting_attendance || {};
                              
                              // Support both new flexible format and old format
                              let yearMeetings = [];
                              let displayYear = currentYear;
                              
                              if (Array.isArray(attendance[currentYear])) {
                                // New flexible format: { "2025": [{ date, status, note }, ...] }
                                yearMeetings = attendance[currentYear];
                                displayYear = currentYear;
                              } else if (attendance.year && attendance.meetings) {
                                // Old format: { year: 2025, meetings: [...] }
                                yearMeetings = attendance.meetings || [];
                                displayYear = attendance.year;
                              } else {
                                // Check for year keys
                                const years = Object.keys(attendance).filter(k => /^\d{4}$/.test(k)).sort((a, b) => b - a);
                                if (years.length > 0) {
                                  displayYear = years[0];
                                  yearMeetings = attendance[displayYear] || [];
                                }
                              }
                              
                              const total = yearMeetings.length;
                              const present = yearMeetings.filter(m => m?.status === 1).length;
                              const excused = yearMeetings.filter(m => m?.status === 2).length;
                              const absent = yearMeetings.filter(m => m?.status === 0).length;
                              
                              return (
                                <>
                                  <span className="text-xs text-white">{displayYear}</span>
                                  <div className="flex items-center gap-1 text-xs">
                                    <span className="text-slate-400">{total}:</span>
                                    <span className="text-green-600 font-medium">P:{present}</span>
                                    <span className="text-yellow-600 font-medium">E:{excused}</span>
                                    <span className="text-red-500">A:{absent}</span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </TableCell>
                      )}
                      {(hasPermission('admin_actions') || userRole === 'admin') && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1.5">
                            {/* Info/View Group - only show if user can edit this member */}
                            {member.can_edit && (
                              <div className="flex gap-1 p-1 bg-slate-700/20 rounded border border-slate-600/30">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-500 hover:text-blue-600 hover:bg-blue-950/50 h-8 w-8 p-0"
                                  onClick={() => handleOpenActions(member)}
                                  title="View/Add Actions"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-purple-500 hover:text-purple-600 hover:bg-purple-950/50 h-8 w-8 p-0"
                                  onClick={() => handleOpenDocuments(member)}
                                  title="Send Documents"
                                  data-testid={`documents-member-${member.id}`}
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-amber-500 hover:text-amber-600 hover:bg-amber-950/50 h-8 w-8 p-0"
                                  onClick={() => handleOpenMemberFiles(member)}
                                  title="Member Files"
                                  data-testid={`files-member-${member.id}`}
                                >
                                  <Paperclip className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-300 hover:text-white hover:bg-slate-600 h-8 w-8 p-0"
                                  onClick={() => handleEdit(member)}
                                  data-testid={`edit-member-${member.id}`}
                                  title="Edit Member"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                            
                            {/* Delete Action - only show if user can edit this member */}
                            {member.can_edit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-600 hover:bg-red-950/50 h-8 w-8 p-0"
                                onClick={() => handleDelete(member)}
                                data-testid={`delete-member-${member.id}`}
                                title="Archive Member"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Actions Dialog */}
        <Dialog open={actionsDialogOpen} onOpenChange={setActionsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Actions History - {selectedMember?.handle}</DialogTitle>
            </DialogHeader>
            {selectedMember && (
              <div className="space-y-6 mt-4">
                {/* Existing Actions List */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">History</h3>
                  {selectedMember.actions && selectedMember.actions.length > 0 ? (
                    <div className="space-y-2">
                      {selectedMember.actions.sort((a, b) => new Date(b.date) - new Date(a.date)).map((action) => (
                        <div key={action.id} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                action.type === 'merit' ? 'bg-green-600 text-white' :
                                action.type === 'promotion' ? 'bg-blue-600 text-white' :
                                'bg-red-600 text-white'
                              }`}>
                                {action.type.toUpperCase()}
                              </span>
                              <span className="text-slate-400 text-sm">{action.date}</span>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-600 hover:text-blue-700"
                                onClick={() => handleEditAction(action)}
                                title="Edit Action"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteAction(action.id)}
                                title="Delete Action"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-slate-200 text-sm mb-2">{action.description}</p>
                          <p className="text-slate-500 text-xs">
                            Added by {action.added_by} on {new Date(action.added_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm">No actions recorded yet.</p>
                  )}
                </div>

                {/* Add/Edit Action Form */}
                <div className="border-t border-slate-700 pt-6">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">
                    {editingAction ? 'Edit Action' : 'Add New Action'}
                  </h3>
                  <form onSubmit={editingAction ? handleUpdateAction : handleAddAction} className="space-y-4">
                    <div>
                      <Label className="text-white">Type</Label>
                      <Select
                        value={actionForm.type}
                        onValueChange={(value) => setActionForm({ ...actionForm, type: value })}
                      >
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue className="text-white" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          <SelectItem value="merit" className="text-white hover:bg-slate-600">Merit</SelectItem>
                          <SelectItem value="promotion" className="text-white hover:bg-slate-600">Promotion</SelectItem>
                          <SelectItem value="disciplinary" className="text-white hover:bg-slate-600">Disciplinary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-white">Date</Label>
                      <Input
                        type="date"
                        value={actionForm.date}
                        onChange={(e) => setActionForm({ ...actionForm, date: e.target.value })}
                        required
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>

                    <div>
                      <Label className="text-white">Description</Label>
                      <Textarea
                        value={actionForm.description}
                        onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })}
                        placeholder="Enter action details..."
                        rows={4}
                        required
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      />
                    </div>

                    <div className="flex gap-3 justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={editingAction ? handleCancelEdit : () => setActionsDialogOpen(false)}
                        className="border-slate-600 text-white hover:bg-slate-700"
                      >
                        {editingAction ? 'Cancel' : 'Close'}
                      </Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                        {editingAction ? 'Update Action' : 'Add Action'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Member Files Dialog (Feb 2026 — officer-curated document attachments) */}
        <Dialog open={filesDialogOpen} onOpenChange={setFilesDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Paperclip className="w-5 h-5 text-amber-400" />
                Files — {selectedMember?.handle}
              </DialogTitle>
            </DialogHeader>
            {selectedMember && (
              <div className="space-y-6 mt-2">
                {/* Existing files list */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider">
                    Attached files
                  </h3>
                  {memberFilesLoading ? (
                    <p className="text-slate-400 text-sm">Loading…</p>
                  ) : memberFiles.length === 0 ? (
                    <p className="text-slate-400 text-sm" data-testid="no-member-files">
                      No files yet — upload one below.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {memberFiles.map((f) => (
                        <div
                          key={f.id}
                          data-testid={`member-file-row-${f.id}`}
                          className="flex items-center justify-between gap-2 p-3 rounded-md border border-slate-700 bg-slate-800/60"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-white text-sm break-all">
                                {f.label || f.original_filename}
                              </span>
                              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-amber-900/30 text-amber-300 border border-amber-800/50">
                                {(f.category || "other").replace("_", " ")}
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                              {f.original_filename} · {((f.size_bytes || 0) / 1024).toFixed(0)} KB ·
                              by {f.uploaded_by_handle || f.uploaded_by_username} ·{" "}
                              {f.uploaded_at?.slice(0, 10)}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                              onClick={() => handleViewMemberFile(f)}
                              title="View"
                              data-testid={`view-member-file-${f.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-amber-400 hover:text-amber-300 h-8 w-8 p-0"
                              onClick={() => handleDownloadMemberFile(f)}
                              title="Download"
                              data-testid={`download-member-file-${f.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {(userChapter === "National" && ["COO", "CTO", "Prez", "VP", "S@A", "ENF"].includes(userTitle)) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                onClick={() => handleDeleteMemberFile(f)}
                                title="Delete (National COO/Prez/VP/S@A/ENF/SEC only)"
                                data-testid={`delete-member-file-${f.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Upload form */}
                <div className="border-t border-slate-700 pt-4 space-y-3">
                  <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                    Upload new file
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300 text-xs">Category</Label>
                      <select
                        data-testid="new-file-category"
                        value={newFileCategory}
                        onChange={(e) => setNewFileCategory(e.target.value)}
                        className="mt-1 w-full bg-slate-900 border border-slate-600 text-white text-sm rounded-md h-9 px-2"
                      >
                        <option value="signed_bylaws">Signed Bylaws</option>
                        <option value="club_forms">Club Forms</option>
                        <option value="officer_issued">Officer Issued</option>
                        <option value="discipline">Discipline</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">
                        Label (optional, defaults to file name)
                      </Label>
                      <Input
                        data-testid="new-file-label"
                        value={newFileLabel}
                        onChange={(e) => setNewFileLabel(e.target.value)}
                        placeholder="e.g. 2025 DOT Medical Card"
                        className="mt-1 bg-slate-900 border-slate-600 text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">File (max 10 MB)</Label>
                    <input
                      data-testid="new-file-input"
                      type="file"
                      accept=".pdf,.doc,.docx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.webp,.heic,.xls,.xlsx,.csv"
                      onChange={(e) => setNewFileObj(e.target.files?.[0] || null)}
                      className="mt-1 block w-full text-sm text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-amber-600 file:text-white hover:file:bg-amber-700 cursor-pointer"
                    />
                    {newFileObj && (
                      <div className="text-[11px] text-slate-400 mt-1">
                        {newFileObj.name} · {(newFileObj.size / 1024).toFixed(0)} KB
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleUploadMemberFile}
                    disabled={!newFileObj || newFileUploading}
                    data-testid="upload-member-file-btn"
                    className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {newFileUploading ? "Uploading…" : "Upload File"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog - Custom Implementation */}
        {deleteDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/80" 
              onClick={() => setDeleteDialogOpen(false)}
            />
            
            {/* Dialog */}
            <div className="relative bg-slate-800 border border-slate-700 rounded-lg p-6 mx-4 max-w-sm w-full shadow-xl">
              {/* Header */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Archive Member</h3>
                <button 
                  onClick={() => setDeleteDialogOpen(false)}
                  className="text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              {memberToDelete && (
                <div className="space-y-4">
                  <p className="text-slate-200 text-sm">
                    You are about to archive <span className="font-semibold text-white">{memberToDelete.handle} - {memberToDelete.name}</span>. 
                    This action will move the member to the archived records.
                  </p>
                  
                  {/* Removal Reason Category Dropdown */}
                  <div>
                    <label className="block text-sm text-slate-300 mb-2">
                      Reason Category *
                    </label>
                    <select
                      value={deleteReasonCategory}
                      onChange={(e) => setDeleteReasonCategory(e.target.value)}
                      required
                      className="w-full p-2 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                    >
                      <option value="">Select a reason...</option>
                      <option value="resigned">Resigned by Email Notification</option>
                      <option value="dues_nonpayment">Not Paying Dues Over 30 Days</option>
                      <option value="member_vote">Removed by Member Vote</option>
                      <option value="national_board">Removed by National Board Vote</option>
                      <option value="inactive">Inactive / No Contact</option>
                      <option value="deceased">Deceased</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-300 mb-2">
                      Comments {deleteReasonCategory === 'other' ? '*' : '(Optional - will be posted to Officers on Discord)'}
                    </label>
                    <textarea
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      placeholder="Enter comments for Discord notification..."
                      rows={2}
                      required={deleteReasonCategory === 'other'}
                      className="w-full p-2 text-sm bg-slate-700 border border-slate-600 rounded text-white placeholder:text-slate-400"
                    />
                  </div>
                  
                  {/* Kick from Discord option */}
                  <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                    <input
                      type="checkbox"
                      id="kickFromDiscord"
                      checked={kickFromDiscord}
                      onChange={(e) => setKickFromDiscord(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-red-600 focus:ring-red-500"
                    />
                    <label htmlFor="kickFromDiscord" className="text-sm text-slate-200 cursor-pointer">
                      Also kick from Discord server
                    </label>
                  </div>
                  
                  {/* Cancel Square Subscription option */}
                  <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                    <input
                      type="checkbox"
                      id="cancelSquareSubscription"
                      checked={cancelSquareSubscription}
                      onChange={(e) => setCancelSquareSubscription(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-amber-600 focus:ring-amber-500"
                    />
                    <label htmlFor="cancelSquareSubscription" className="text-sm text-slate-200 cursor-pointer">
                      Cancel Square subscription (if any)
                    </label>
                  </div>
                  
                  {/* Buttons */}
                  <div className="flex gap-3 justify-end pt-4 border-t border-slate-700">
                    <button
                      type="button"
                      onClick={() => setDeleteDialogOpen(false)}
                      className="px-4 py-2 text-sm border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmDelete}
                      disabled={!deleteReasonCategory || (deleteReasonCategory === 'other' && !deleteReason.trim())}
                      className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {kickFromDiscord ? "Archive & Kick" : "Archive Member"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open) {
            setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
            setPasswordError("");
            setPasswordSuccess("");
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {passwordError && (
                <div className="p-3 bg-red-900/50 border border-red-600 rounded text-red-200 text-sm">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="p-3 bg-green-900/50 border border-green-600 rounded text-green-200 text-sm">
                  {passwordSuccess}
                </div>
              )}
              <div>
                <Label className="text-slate-200">Current Password</Label>
                <Input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-200">New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-200">Confirm New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setPasswordDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleChangePassword}
                  disabled={!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                >
                  Change Password
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Documents Dialog */}
        <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <i className="fas fa-file-signature text-purple-400"></i>
                Documents - {selectedMember?.handle}
              </DialogTitle>
              <DialogDescription>
                Send and track documents for signing
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Send New Document Button */}
              {(userPermissions?.send_documents || userRole === 'admin') && documentTemplates.length > 0 && (
                <Button 
                  onClick={() => setSendDocDialogOpen(true)}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="send-new-document-btn"
                >
                  <i className="fas fa-paper-plane mr-2"></i>
                  Send New Document
                </Button>
              )}
              
              {/* Documents List */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-300">Document History</h4>
                {loadingDocuments ? (
                  <div className="text-center py-4 text-slate-400">Loading...</div>
                ) : memberDocuments.length === 0 ? (
                  <div className="text-center py-4 text-slate-400 text-sm">
                    No documents sent to this member yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {memberDocuments.map((doc) => (
                      <div key={doc.id} className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-white text-sm">{doc.template_name}</div>
                            <div className="text-xs text-slate-400">
                              Sent {new Date(doc.sent_at).toLocaleDateString()} by {doc.sent_by}
                            </div>
                            {doc.signed_at && (
                              <div className="text-xs text-green-400">
                                Recipient signed {new Date(doc.signed_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {getDocumentStatusBadge(doc)}
                          </div>
                        </div>
                        
                        {/* Approval Chain Progress */}
                        {doc.approval_chain && doc.approval_chain.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-600">
                            <div className="text-xs text-slate-400 mb-2">Approval Chain:</div>
                            <div className="flex flex-wrap gap-2">
                              {doc.approval_chain.map((approver, idx) => (
                                <div 
                                  key={idx} 
                                  className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                                    approver.status === 'approved' ? 'bg-green-600/30 text-green-300' :
                                    approver.status === 'denied' ? 'bg-red-600/30 text-red-300' :
                                    approver.status === 'pending' && idx === (doc.current_approver_index - 1) ? 'bg-orange-600/30 text-orange-300 animate-pulse' :
                                    'bg-slate-600/30 text-slate-400'
                                  }`}
                                >
                                  {approver.status === 'approved' && <i className="fas fa-check"></i>}
                                  {approver.status === 'denied' && <i className="fas fa-times"></i>}
                                  {approver.status === 'pending' && idx === (doc.current_approver_index - 1) && <i className="fas fa-clock"></i>}
                                  {approver.status === 'pending' && idx !== (doc.current_approver_index - 1) && <i className="fas fa-hourglass-half"></i>}
                                  <span>{approver.title}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="mt-3 pt-3 border-t border-slate-600 flex gap-2">
                          <button
                            onClick={() => handleViewDocDetails(doc)}
                            className="flex-1 px-3 py-1.5 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors flex items-center justify-center gap-1"
                          >
                            <Eye className="w-3 h-3" />
                            View Details
                          </button>
                          {(doc.status === 'completed' || doc.status === 'signed') && (
                            <button
                              onClick={() => handleDownloadSignedDoc(doc.id)}
                              className="flex-1 px-3 py-1.5 text-xs bg-green-600 hover:bg-green-500 text-white rounded transition-colors flex items-center justify-center gap-1"
                            >
                              <Download className="w-3 h-3" />
                              Download Signed
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteDocumentRecord(doc.id, doc.status)}
                            className="px-3 py-1.5 text-xs bg-red-600/30 hover:bg-red-600/50 text-red-300 rounded transition-colors"
                            title={["cancelled", "completed", "expired", "denied"].includes(doc.status) ? "Delete permanently" : "Cancel request"}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDocumentsDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Document Dialog */}
        <Dialog open={sendDocDialogOpen} onOpenChange={setSendDocDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Send Document for Signing</DialogTitle>
              <DialogDescription>
                Select a document to send to {selectedMember?.handle}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-slate-200">Send To Email</Label>
                <select
                  value={selectedEmail}
                  onChange={(e) => setSelectedEmail(e.target.value)}
                  className="w-full mt-1 p-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                >
                  <option value="">Select email address...</option>
                  {selectedMember?.personal_email && (
                    <option value={selectedMember.personal_email}>
                      {selectedMember.personal_email} (Personal)
                    </option>
                  )}
                  {selectedMember?.email && selectedMember.email !== selectedMember?.personal_email && (
                    <option value={selectedMember.email}>
                      {selectedMember.email} (Primary)
                    </option>
                  )}
                </select>
              </div>
              <div>
                <Label className="text-slate-200">Document Template</Label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="w-full mt-1 p-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                >
                  <option value="">Select a template...</option>
                  {documentTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-slate-200">Message (Optional)</Label>
                <textarea
                  value={docMessage}
                  onChange={(e) => setDocMessage(e.target.value)}
                  placeholder="Add a personal message..."
                  rows={2}
                  className="w-full mt-1 p-2 bg-slate-700 border border-slate-600 rounded text-white placeholder:text-slate-400 text-sm"
                />
              </div>
              
              {/* Approval Chain Section */}
              <div className="border-t border-slate-700 pt-4">
                <Label className="text-slate-200 flex items-center gap-2">
                  <i className="fas fa-user-check text-purple-400"></i>
                  Approval Chain (Optional)
                </Label>
                <p className="text-xs text-slate-400 mt-1 mb-3">
                  Select National Officers who must approve this document after the recipient signs.
                  Order matters - click arrows to reorder.
                </p>
                
                {/* Available Officers */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {nationalOfficers.map((officer) => {
                    const isSelected = selectedApprovers.includes(officer.role);
                    const hasUser = officer.user !== null;
                    
                    return (
                      <button
                        key={officer.role}
                        type="button"
                        disabled={!hasUser}
                        onClick={() => toggleApprover(officer.role)}
                        className={`p-2 rounded text-left text-xs transition-all ${
                          !hasUser 
                            ? 'bg-slate-800 text-slate-500 cursor-not-allowed' 
                            : isSelected 
                              ? 'bg-purple-600 text-white' 
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        <div className="font-medium truncate">{officer.display_title}</div>
                        <div className="text-xs opacity-70 truncate">
                          {hasUser ? officer.user.username : '(No officer assigned)'}
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                {/* Selected Approvers Order */}
                {selectedApprovers.length > 0 && (
                  <div className="bg-slate-800 rounded-lg p-3">
                    <div className="text-xs text-slate-400 mb-2">Approval Order:</div>
                    <div className="space-y-1">
                      {selectedApprovers.map((role, index) => {
                        const officer = nationalOfficers.find(o => o.role === role);
                        return (
                          <div key={role} className="flex items-center gap-2 bg-slate-700/50 rounded p-2">
                            <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center">
                              {index + 1}
                            </span>
                            <span className="flex-1 text-sm text-slate-200">{officer?.display_title}</span>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => moveApproverUp(index)}
                                disabled={index === 0}
                                className="p-1 text-slate-400 hover:text-white disabled:opacity-30"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveApproverDown(index)}
                                disabled={index === selectedApprovers.length - 1}
                                className="p-1 text-slate-400 hover:text-white disabled:opacity-30"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleApprover(role)}
                                className="p-1 text-slate-400 hover:text-red-400"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      After recipient signs, document will go to each approver in order.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setSendDocDialogOpen(false); setSelectedApprovers([]); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendDocument}
                disabled={!selectedTemplate || !selectedEmail}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Send{selectedApprovers.length > 0 ? ` (${selectedApprovers.length} approvers)` : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Document Details Dialog */}
        <Dialog open={viewDocDetailsOpen} onOpenChange={setViewDocDetailsOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-400" />
                Document Details
              </DialogTitle>
              <DialogDescription>
                {selectedDocDetails?.template_name}
              </DialogDescription>
            </DialogHeader>
            
            {selectedDocDetails && (
              <div className="space-y-4">
                {/* Status Badge */}
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Status:</span>
                  {getDocumentStatusBadge(selectedDocDetails)}
                </div>
                
                {/* Basic Info */}
                <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Recipient:</span>
                    <span className="text-white">{selectedDocDetails.recipient_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Email:</span>
                    <span className="text-white text-xs">{selectedDocDetails.recipient_email}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Sent:</span>
                    <span className="text-white">{new Date(selectedDocDetails.sent_at).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Sent by:</span>
                    <span className="text-white">{selectedDocDetails.sent_by}</span>
                  </div>
                  {selectedDocDetails.signed_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Recipient Signed:</span>
                      <span className="text-green-400">{new Date(selectedDocDetails.signed_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
                
                {/* Recipient Audit Trail */}
                {selectedDocDetails.audit_trail && (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="text-sm font-medium text-white mb-2">Recipient Signature</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Signer:</span>
                        <span className="text-white">{selectedDocDetails.audit_trail.signer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Type:</span>
                        <span className="text-white">{selectedDocDetails.audit_trail.signature_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">IP:</span>
                        <span className="text-white">{selectedDocDetails.audit_trail.ip_address}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Signed:</span>
                        <span className="text-white">{new Date(selectedDocDetails.audit_trail.signed_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Approval Chain Details */}
                {selectedDocDetails.approval_chain && selectedDocDetails.approval_chain.length > 0 && (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="text-sm font-medium text-white mb-3">Approval Chain</div>
                    <div className="space-y-3">
                      {selectedDocDetails.approval_chain.map((approver, idx) => (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border ${
                            approver.status === 'approved' ? 'border-green-600 bg-green-600/10' :
                            approver.status === 'denied' ? 'border-red-600 bg-red-600/10' :
                            'border-slate-600 bg-slate-800/50'
                          }`}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center">
                                {idx + 1}
                              </span>
                              <span className="text-white font-medium text-sm">{approver.title}</span>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              approver.status === 'approved' ? 'bg-green-600 text-white' :
                              approver.status === 'denied' ? 'bg-red-600 text-white' :
                              'bg-slate-600 text-slate-300'
                            }`}>
                              {approver.status === 'approved' ? 'Approved' : 
                               approver.status === 'denied' ? 'Denied' : 'Pending'}
                            </span>
                          </div>
                          
                          {approver.username && (
                            <div className="text-xs text-slate-400 mb-1">
                              Assigned to: {approver.username}
                            </div>
                          )}
                          
                          {approver.signed_at && (
                            <div className="text-xs text-slate-400">
                              Signed: {new Date(approver.signed_at).toLocaleString()}
                            </div>
                          )}
                          
                          {approver.typed_name && (
                            <div className="text-xs text-slate-400">
                              Signature: {approver.typed_name}
                            </div>
                          )}
                          
                          {approver.notes && (
                            <div className="mt-2 p-2 bg-slate-800/50 rounded text-xs">
                              <span className="text-slate-400">Notes: </span>
                              <span className="text-white">{approver.notes}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Final Decision */}
                {selectedDocDetails.final_decision && (
                  <div className={`p-4 rounded-lg ${
                    selectedDocDetails.final_decision === 'approved' ? 'bg-green-600/20 border border-green-600' : 'bg-red-600/20 border border-red-600'
                  }`}>
                    <div className="flex items-center gap-2">
                      <i className={`fas fa-${selectedDocDetails.final_decision === 'approved' ? 'check-circle text-green-400' : 'times-circle text-red-400'} text-xl`}></i>
                      <div>
                        <div className={`font-medium ${selectedDocDetails.final_decision === 'approved' ? 'text-green-400' : 'text-red-400'}`}>
                          Final Decision: {selectedDocDetails.final_decision.toUpperCase()}
                        </div>
                        {selectedDocDetails.final_notes && (
                          <div className="text-sm text-slate-300 mt-1">{selectedDocDetails.final_notes}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter className="flex gap-2">
              {selectedDocDetails && (selectedDocDetails.status === 'completed' || selectedDocDetails.status === 'signed') && (
                <Button 
                  onClick={() => handleDownloadSignedDoc(selectedDocDetails.id)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Signed PDF
                </Button>
              )}
              <Button variant="outline" onClick={() => setViewDocDetailsOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}